// [PURPOSE]: Centralized registry of SRD subclass features with mechanical effects.
// [WHY]: srdCharacterData.js stores subclass features as plain string arrays
//   ({ 3: ['Improved Critical'], 7: ['Remarkable Athlete'] }). This file maps each
//   named feature to its effects array so the effectRegistry can register and
//   consume them uniformly — mirrors srdClassFeatures.js (commit 81c2ef9) and
//   srdRaceFeatures.js (commit d1a0801) for the subclass side of the effect system.
// [INTEGRATION]: useSubclassFeatureEffects hook reads this on subclass/level/
//   subclassData change and registers effects with prefix
//   'subclass:<ClassName>:<SubclassName>:<FeatureName>' (and ':<OptionName>' for
//   choice features).
// [Phase 4b bucket 3 — APR 2026]: Comprehensive coverage of all 92 SRD subclasses
//   (PHB core + Tasha's + Xanathar's). See spec
//   docs/superpowers/specs/2026-04-25-phase4b-bucket3-subclass-infrastructure-design.md.
//
// SCHEMA:
//   SRD_SUBCLASS_FEATURES[className][subclassName] = {
//     source: 'srd' | 'tashas' | 'xanathars',     // documentation-only
//     [featureName]: {
//       minLevel: number,                          // class level the feature is gained
//       effects?: Effect[],                        // always-on effects
//       choices?: { [optionName]: { effects, notes? } },
//       choiceKey?: string,                        // dotted path inside subclassData
//       notes: string,                             // MANDATORY audit trail
//     }
//   }
//
// INVARIANTS:
//   - Every feature entry has a 'notes' field. No exceptions.
//   - effects: [] (empty) is the EXPLICIT signal "audited, intentionally narrative."
//     Never omit a feature from the registry — always add with notes.
//   - 'source' is the only top-level subclass key that is NOT a feature.
//     getActiveSubclassFeatures filters it out.

export const SRD_SUBCLASS_FEATURES = {
  Barbarian: {
    'Path of the Berserker': {
      source: 'srd',
      'Frenzy': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'berserker_frenzy_extra_attack', condition: { when: 'raging' } },
        ],
        notes: 'PHB p.49 — While raging you may frenzy: bonus action melee attack each turn for the rage duration. Suffer 1 level of exhaustion when rage ends. Action-economy + post-rage exhaustion handled by combat panel + rest flow. [BREADCRUMB] Exhaustion-on-rage-end hook needed.',
      },
      'Mindless Rage': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'berserker_mindless_rage_charm_fear_immunity', condition: { when: 'raging' } },
        ],
        notes: "PHB p.49 — Can't be charmed or frightened while raging; suspended conditions resume when rage ends. Encoded as a feature flag because the registry does not currently distinguish immunity from resistance (mapping table line 1001).",
      },
      'Intimidating Presence': {
        minLevel: 10,
        effects: [],
        notes: 'PHB p.49 — Action: frighten one creature within 30 ft (WIS save vs 8+CON+prof). Action-economy + save-DC combat handler. [BREADCRUMB] DC consumer.',
      },
      'Retaliation': {
        minLevel: 14,
        effects: [],
        notes: 'PHB p.49 — Reaction: melee attack vs creature within 5 ft that damaged you. Reaction handler in combat panel.',
      },
    },

    'Path of the Totem Warrior': {
      source: 'srd',
      'Spirit Seeker': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'totem_spirit_seeker_beast_speech' },
          // [Phase 4b BC-13 closeout — APR 2026]: Ritual-only spell grants.
          //   Player gains the ability to cast these spells as rituals even
          //   though they aren't on the Barbarian spell list. Wired via
          //   ritualSpell consumer (effectCalculator.getGrantedRitualSpells)
          //   which canRitualCast in App.jsx consults.
          { type: 'ritualSpell', operation: 'grant', value: 'Beast Sense' },
          { type: 'ritualSpell', operation: 'grant', value: 'Speak with Animals' },
        ],
        notes: 'PHB p.50 — Cast Beast Sense and Speak with Animals as rituals. Wired via ritualSpell effect.',
      },
      'Totem Spirit': {
        minLevel: 3,
        choiceKey: 'totemSpirit',
        choices: {
          'Bear': {
            effects: [
              { type: 'resistance', operation: 'grant', value: 'bludgeoning', condition: { when: 'raging' } },
              { type: 'resistance', operation: 'grant', value: 'piercing',    condition: { when: 'raging' } },
              { type: 'resistance', operation: 'grant', value: 'slashing',    condition: { when: 'raging' } },
              { type: 'resistance', operation: 'grant', value: 'fire',        condition: { when: 'raging' } },
              { type: 'resistance', operation: 'grant', value: 'cold',        condition: { when: 'raging' } },
              { type: 'resistance', operation: 'grant', value: 'lightning',   condition: { when: 'raging' } },
              { type: 'resistance', operation: 'grant', value: 'thunder',     condition: { when: 'raging' } },
              { type: 'resistance', operation: 'grant', value: 'acid',        condition: { when: 'raging' } },
              { type: 'resistance', operation: 'grant', value: 'poison',      condition: { when: 'raging' } },
              { type: 'resistance', operation: 'grant', value: 'necrotic',    condition: { when: 'raging' } },
              { type: 'resistance', operation: 'grant', value: 'radiant',     condition: { when: 'raging' } },
              { type: 'resistance', operation: 'grant', value: 'force',       condition: { when: 'raging' } },
            ],
            notes: 'PHB p.50 — Resistance to all damage except psychic while raging.',
          },
          'Eagle': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'eagle_totem_dash_bonus_action', condition: { when: 'raging' } },
              { type: 'feature', operation: 'grant', value: 'eagle_totem_oa_disadvantage', condition: { when: 'raging' } },
            ],
            notes: 'PHB p.50 — Opportunity attacks against you have disadvantage; can Dash as bonus action while raging and not in heavy armor. Surfaced via hasFeatureFlag.',
          },
          'Wolf': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'wolf_totem_pack_tactics', condition: { when: 'raging' } },
            ],
            notes: 'PHB p.50 — Allies have advantage on melee attacks vs targets within 5 ft of you while raging.',
          },
        },
        notes: 'PHB p.50 — Choose Bear, Eagle, or Wolf totem. Resolved via subclassData.totemSpirit.',
      },
      'Aspect of the Beast': {
        minLevel: 6,
        choiceKey: 'totemSpirit',
        choices: {
          'Bear': {
            effects: [
              { type: 'carryCapacity', operation: 'multiply', value: 2 },
              { type: 'feature', operation: 'grant', value: 'bear_totem_aspect_str_advantage' },
            ],
            notes: 'PHB p.50 — Carrying capacity doubled; advantage on STR checks for push/pull/lift/break.',
          },
          'Eagle': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'eagle_totem_aspect_keen_sight' },
            ],
            notes: 'PHB p.50 — See clearly up to 1 mile in good lighting; dim light penalty halved on Perception (sight).',
          },
          'Wolf': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'wolf_totem_aspect_tracker' },
            ],
            notes: 'PHB p.50 — Track at fast pace + stealthy at normal pace.',
          },
        },
        notes: 'PHB p.50 — At L6 your Aspect choice grants a passive benefit per totem.',
      },
      'Spirit Walker': {
        minLevel: 10,
        effects: [
          // [Phase 4b BC-13 closeout — APR 2026]: Ritual-only Commune with
          //   Nature. Same wiring as Spirit Seeker.
          { type: 'ritualSpell', operation: 'grant', value: 'Commune with Nature' },
        ],
        notes: 'PHB p.50 — Cast Commune with Nature as ritual. Wired via ritualSpell effect.',
      },
      'Totemic Attunement': {
        minLevel: 14,
        choiceKey: 'totemSpirit',
        choices: {
          'Bear': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'bear_totemic_attunement_taunt', condition: { when: 'raging' } },
            ],
            notes: 'PHB p.50 — Hostile creatures within 5 ft have disadvantage on attacks vs your allies while raging.',
          },
          'Eagle': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'eagle_totemic_attunement_fly', condition: { when: 'raging' } },
            ],
            notes: 'PHB p.50 — Fly speed = walking speed while raging (must land at end of turn or fall).',
          },
          'Wolf': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'wolf_totemic_attunement_knockdown', condition: { when: 'raging' } },
            ],
            notes: 'PHB p.50 — Bonus action: knock target prone after melee weapon hit while raging.',
          },
        },
        notes: 'PHB p.50 — At L14 your Attunement choice grants a capstone benefit per totem.',
      },
    },

    'Path of the Beast': {
      source: 'tashas',
      'Form of the Beast': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'beast_form_natural_weapons', condition: { when: 'raging' } },
        ],
        notes: 'TCE p.10 — When you rage, manifest natural weapons (bite/claws/tail) as the rage activates. Bite 1d8 piercing + heal CON-mod HP once per turn; claws 1d6 slashing with extra attack; tail 1d8 piercing reach 10 ft + reaction +1d8 AC. Action-economy/dice handled by combat panel; flag exposes the form. [BREADCRUMB] Per-form damage roll handler.',
      },
      'Bestial Soul': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'beast_bestial_soul_natural_magical' },
        ],
        notes: 'TCE p.10 — Natural-weapon attacks count as magical for resistance/immunity bypass. Plus pick a swim/climb/jump benefit per long rest. [BREADCRUMB] Magic-attack flag for combat resolution; alt-mode picker UI.',
      },
      'Infectious Fury': {
        minLevel: 10,
        effects: [],
        notes: 'TCE p.10 — On natural-weapon hit while raging, force WIS save or psychic 2d12 / re-attack ally. WIS save resolution; PB uses/long rest. [BREADCRUMB] Save-DC + uses tracker.',
      },
      'Call the Hunt': {
        minLevel: 14,
        effects: [],
        notes: 'TCE p.10 — When you rage, grant CON-mod allies within 30 ft +1d6 damage on first hit per turn; you gain 5*allies temp HP. Per-rest = PB uses. [BREADCRUMB] Aura-grant tracking + temp-HP application.',
      },
    },

    'Path of Wild Magic': {
      source: 'tashas',
      'Magic Awareness': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wild_magic_awareness_detect_magic' },
        ],
        notes: 'TCE p.11 — Action: sense schools of magic within 60 ft (PB times per long rest). [BREADCRUMB] Detect-magic action handler + per-rest counter.',
      },
      'Wild Surge': {
        minLevel: 3,
        effects: [],
        notes: 'TCE p.11 — When you rage, roll d8 on Wild Magic table for a magical effect lasting until rage ends. [BREADCRUMB] Wild Magic surge table + per-rage roll. Each surge effect would itself benefit from registry wiring (separate ticket).',
      },
      'Bolstering Magic': {
        minLevel: 6,
        effects: [],
        notes: 'TCE p.11 — Action: touch ally — either +1d3 to attack rolls and ability checks for 10 min, or roll d3 to regain spell slot of that level. Per-rest = PB uses. [BREADCRUMB] Spell-slot restore + buff timer.',
      },
      'Unstable Backlash': {
        minLevel: 10,
        effects: [],
        notes: 'TCE p.11 — Reaction when damaged or failed save while raging: re-roll on Wild Magic table to replace current surge. Reaction handler.',
      },
      'Controlled Surge': {
        minLevel: 14,
        effects: [],
        notes: 'TCE p.11 — When you roll Wild Magic surge, roll twice and pick. UI wiring on the surge table.',
      },
    },

    'Path of the Ancestral Guardian': {
      source: 'xanathars',
      'Ancestral Protectors': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'ancestral_protectors_first_target_disadvantage', condition: { when: 'raging' } },
        ],
        notes: 'XGE p.9 — First creature you hit on a turn while raging has disadvantage on attacks against anyone but you, and your allies have resistance to that creature\'s damage. Combat-tracker + per-attack target marking. [BREADCRUMB] Mark/resist handler.',
      },
      'Spirit Shield': {
        minLevel: 6,
        effects: [],
        notes: 'XGE p.9 — Reaction while raging: reduce damage to ally within 30 ft by 2d6 (3d6 L10, 4d6 L14). Reaction handler.',
      },
      'Consult the Spirits': {
        minLevel: 10,
        effects: [],
        notes: 'XGE p.10 — Cast Augury or Clairvoyance without a slot, WIS-based; once per short or long rest. [BREADCRUMB] Per-rest spell grant via grantedSpells.',
      },
      'Vengeful Ancestors': {
        minLevel: 14,
        effects: [],
        notes: 'XGE p.10 — Spirit Shield damage reduction is also reflected as force damage to attacker. Combat-tracker reflect handler.',
      },
    },

    'Path of the Storm Herald': {
      source: 'xanathars',
      'Storm Aura': {
        minLevel: 3,
        choiceKey: 'stormAura',
        choices: {
          'Desert': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'storm_aura_desert_fire_damage', condition: { when: 'raging' } },
            ],
            notes: 'XGE p.10 — Desert aura: each creature within 10 ft takes fire damage = 2 (scales). [BREADCRUMB] Aura damage tick.',
          },
          'Sea': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'storm_aura_sea_lightning_strike', condition: { when: 'raging' } },
            ],
            notes: 'XGE p.10 — Sea aura: pick a creature, DEX save or 1d6 lightning (scales). Save handler + targeting.',
          },
          'Tundra': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'storm_aura_tundra_temp_hp', condition: { when: 'raging' } },
            ],
            notes: 'XGE p.10 — Tundra aura: choose creatures within 10 ft, each gains 2 temp HP (scales). Temp-HP application.',
          },
        },
        notes: 'XGE p.10 — At L3 choose Desert/Sea/Tundra aura. Resolved via subclassData.stormAura.',
      },
      'Storm Soul': {
        minLevel: 6,
        choiceKey: 'stormAura',
        choices: {
          'Desert': {
            effects: [
              { type: 'resistance', operation: 'grant', value: 'fire' },
            ],
            notes: 'XGE p.10 — Resistance to fire damage; you and items you wear/carry are unaffected by extreme heat; can ignite flammable objects with a touch.',
          },
          'Sea': {
            effects: [
              { type: 'resistance', operation: 'grant', value: 'lightning' },
              { type: 'feature', operation: 'grant', value: 'storm_soul_sea_swim_speed' },
            ],
            notes: 'XGE p.10 — Resistance to lightning damage; can breathe underwater; swim speed = walking speed.',
          },
          'Tundra': {
            effects: [
              { type: 'resistance', operation: 'grant', value: 'cold' },
            ],
            notes: 'XGE p.10 — Resistance to cold damage; you and carried items unaffected by extreme cold; can turn 5-ft cube of water into ice.',
          },
        },
        notes: 'XGE p.10 — At L6 the chosen aura grants passive resistance + an environmental ability.',
      },
      'Shielding Storm': {
        minLevel: 10,
        effects: [],
        notes: 'XGE p.10 — While raging, allies within Storm Aura range gain the Storm Soul resistance for the chosen environment. [BREADCRUMB] Aura-grant resistance to allies — needs ally-target effect type.',
      },
      'Raging Storm': {
        minLevel: 14,
        effects: [],
        notes: 'XGE p.10 — Aura gains a tier-2 effect: Desert reaction fire damage on creature attack, Sea reaction knock-prone, Tundra speed-zero on save fail. Combat-tracker reaction.',
      },
    },

    'Path of the Zealot': {
      source: 'xanathars',
      'Divine Fury': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'zealot_divine_fury_bonus_damage', condition: { when: 'raging' } },
        ],
        notes: 'XGE p.11 — While raging, first weapon hit on a turn deals an extra 1d6 + half-Barbarian-level damage (radiant or necrotic, your choice at character creation). Combat damage handler. [BREADCRUMB] Per-turn first-hit tracking + damage-type choice.',
      },
      'Warrior of the Gods': {
        minLevel: 3,
        effects: [],
        notes: 'XGE p.11 — A spell that returns you to life (Raise Dead, Revivify, Resurrection, etc.) doesn\'t require material components when cast on you. Narrative — surfaces in spellcasting cost waiver, not registry. [BREADCRUMB] Material-cost waiver hook.',
      },
      'Fanatical Focus': {
        minLevel: 6,
        effects: [],
        notes: 'XGE p.11 — Reroll a failed save while raging, must use new roll; once per rage. Save-handler reroll.',
      },
      'Zealous Presence': {
        minLevel: 10,
        effects: [],
        notes: 'XGE p.11 — Bonus action: shout to grant up to 10 allies within 60 ft advantage on attacks and saves until start of your next turn. Once per long rest. [BREADCRUMB] Aura-grant advantage to allies.',
      },
      'Rage Beyond Death': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'zealot_rage_beyond_death', condition: { when: 'raging' } },
        ],
        notes: 'XGE p.11 — Don\'t die from 0 HP while raging (you fall unconscious only when rage ends). Death-save handler must consult this flag. [BREADCRUMB] Death-save bypass while raging.',
      },
    },
  },
  Bard: {
    'College of Lore': {
      source: 'srd',
      'Bonus Proficiencies': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_lore_3_skills_player_choice' },
        ],
        notes: 'PHB p.54 — Gain proficiency with three skills of your choice. Skills are player-chosen via the wizard picker — registering a generic feature flag here is the audit signal; the wizard handles the actual proficiency grants. [BREADCRUMB] Wizard skill picker for Bard:Lore L3.',
      },
      'Cutting Words': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_lore_cutting_words_reaction' },
        ],
        notes: "PHB p.54 — Reaction (within 60 ft, must hear you): expend one Bardic Inspiration die and subtract the roll from a creature's attack roll, ability check, or damage roll. Per-attack reaction consumer; resource is shared with class-level Bardic Inspiration. [BREADCRUMB] Reaction-on-target-roll consumer.",
      },
      'Additional Magical Secrets': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.54 — Learn two spells from any class (count as Bard spells, do not count against spells known). Already wired through the existing Magical Secrets spell-pick mechanism — no subclass-registry effect needed.',
      },
      'Peerless Skill': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_lore_peerless_skill_self_inspiration' },
        ],
        notes: "PHB p.54 — When making an ability check, you can expend one Bardic Inspiration die and add the roll to your check, deciding after seeing the d20 result but before the DM says success/fail. Per-check self-target consumer; shared resource with Bardic Inspiration. [BREADCRUMB] Self-target post-roll inspiration consumer.",
      },
    },

    'College of Valor': {
      source: 'srd',
      'Bonus Proficiencies': {
        minLevel: 3,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'armor:medium', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'armor:shield', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'weapon:martial', proficiencyLevel: 1 },
        ],
        notes: 'PHB p.55 — Gain proficiency with medium armor, shields, and martial weapons.',
      },
      'Combat Inspiration': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_valor_combat_inspiration_attack_or_ac' },
        ],
        notes: "PHB p.55 — A creature holding your Bardic Inspiration die can roll it and add the result to a weapon damage roll, or as a reaction add it to AC against an incoming attack (decided after seeing the attack but before hit/miss). Per-attack ally-target consumer; shared resource with Bardic Inspiration. [BREADCRUMB] Ally-target attack/AC reaction consumer.",
      },
      'Extra Attack': {
        minLevel: 6,
        effects: [
          { type: 'extra_attack', operation: 'add', value: 1 },
        ],
        notes: 'PHB p.55 — Attack twice instead of once when taking the Attack action.',
      },
      'Battle Magic': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_valor_battle_magic_bonus_weapon_attack' },
        ],
        notes: 'PHB p.55 — When you use your action to cast a Bard spell, you can make one weapon attack as a bonus action. Per-turn action-economy consumer.',
      },
    },

    'College of Creation': {
      source: 'tashas',
      'Mote of Potential': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_creation_mote_of_potential_extra_die' },
        ],
        notes: "TCE p.27 — When you give a creature a Bardic Inspiration die, a mote of potential appears next to them, granting one of three benefits depending on use (ability check → roll twice and pick higher; attack roll → AoE thunder save vs the die; saving throw → temp HP equal to the die). Per-use consumer driven by which Bardic Inspiration usage triggers it. [BREADCRUMB] Mote-side-effect consumer keyed to Bardic Inspiration usage type.",
      },
      'Performance of Creation': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_creation_performance_of_creation_object' },
        ],
        notes: "TCE p.27 — Action: create a non-magical item up to Large size, value ≤ 20×bardLevel gp, lasts hours = proficiency bonus. Once per long rest, plus an additional use per spell slot expended. Object-creation consumer; size scales by level (Medium L3, Large L6, Huge L14).",
      },
      'Animating Performance': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_creation_animating_performance_dancing_item' },
        ],
        notes: 'TCE p.27 — Action: animate a Large-or-smaller non-magical item as a Dancing Item creature for 1 hour, once per long rest (additional uses by expending a 3rd-level spell slot). Companion-summon consumer.',
      },
      'Creative Crescendo': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_creation_creative_crescendo_extra_items' },
        ],
        notes: 'TCE p.27 — Performance of Creation can produce CHA-mod items (min 1) at once, with no gp limit. Per-feature scaling consumer.',
      },
    },

    'College of Eloquence': {
      source: 'tashas',
      'Silver Tongue': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_eloquence_silver_tongue_persuasion_deception_min_10' },
        ],
        notes: 'TCE p.28 — When you make a Persuasion or Deception check, treat a d20 roll of 9 or lower as a 10. Skill-roll floor consumer. [BREADCRUMB] Persuasion/Deception roll-floor of 10.',
      },
      'Unsettling Words': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_eloquence_unsettling_words_subtract_inspiration' },
        ],
        notes: "TCE p.28 — Bonus action: expend a Bardic Inspiration die, choose a creature within 60 ft you can see; it must subtract that die from its next saving throw before the start of your next turn. Per-target reaction-style consumer; shared resource with Bardic Inspiration.",
      },
      'Unfailing Inspiration': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_eloquence_unfailing_inspiration_die_returns_on_fail' },
        ],
        notes: 'TCE p.28 — When a creature adds a Bardic Inspiration die from you to a roll and still fails, the creature keeps the die. Per-use bookkeeping consumer.',
      },
      'Universal Speech': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_eloquence_universal_speech_understand_you' },
        ],
        notes: 'TCE p.28 — Action: choose CHA-mod (min 1) creatures within 60 ft; for 1 hour they understand any language you speak. Once per long rest (additional uses by expending a 1st-level-or-higher spell slot). Per-rest tracker.',
      },
      'Infectious Inspiration': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_eloquence_infectious_inspiration_chain' },
        ],
        notes: 'TCE p.28 — Reaction: when a creature within 60 ft succeeds on a roll using your Bardic Inspiration, give a die to another creature within 60 ft without expending a use. Uses = CHA-mod per long rest. Per-rest tracker + chain-grant consumer.',
      },
    },

    'College of Glamour': {
      source: 'xanathars',
      'Mantle of Inspiration': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_glamour_mantle_of_inspiration_temp_hp_move' },
        ],
        notes: "XGE p.14 — Bonus action: expend one Bardic Inspiration; choose CHA-mod creatures within 60 ft; each gains 5/8/11/14 temp HP (by tier) and can use its reaction to move up to its speed without provoking opportunity attacks. Per-grant ally-side consumer; shared resource with Bardic Inspiration.",
      },
      'Enthralling Performance': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_glamour_enthralling_performance_charm' },
        ],
        notes: 'XGE p.14 — 1-minute performance: up to CHA-mod humanoids within 60 ft must succeed a WIS save or be charmed for 1 hour (regards you as friendly acquaintance). Once per short or long rest. Per-rest tracker + charm-state consumer.',
      },
      'Mantle of Majesty': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_glamour_mantle_of_majesty_command' },
        ],
        notes: 'XGE p.14 — Bonus action: cast Command without a slot; for 1 minute (concentration) you can recast Command as a bonus action each turn, with WIS save penalty for charmed targets. Once per long rest. Per-rest tracker + concentration consumer.',
      },
      'Unbreakable Majesty': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_glamour_unbreakable_majesty_attacker_save' },
        ],
        notes: "XGE p.14 — Bonus action: assume a magically majestic presence for 1 minute; an attacker must succeed a CHA save or pick a different target (its action is wasted on a fail). Once per short or long rest. Per-attack save consumer.",
      },
    },

    'College of Swords': {
      source: 'xanathars',
      'Bonus Proficiencies': {
        minLevel: 3,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'armor:medium', proficiencyLevel: 1 },
          { type: 'feature', operation: 'grant', value: 'bard_swords_scimitar_proficiency' },
        ],
        notes: 'XGE p.16 — Gain proficiency with medium armor and the scimitar. We register medium armor through the granular proficiency grant; scimitar proficiency is recorded as a feature flag because the proficiency registry uses category-level keys (weapon:simple/weapon:martial), not per-weapon ids — the wizard treats the flag as the source of truth for scimitar specifically. [BREADCRUMB] Per-weapon scimitar proficiency consumer.',
      },
      'Fighting Style': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_swords_fighting_style_dueling_or_twf' },
        ],
        notes: 'XGE p.16 — Choose Dueling or Two-Weapon Fighting. Player chooses via wizard; the underlying fighting-style mechanism handles the actual stat effect. [BREADCRUMB] Wizard fighting-style picker for Bard:Swords.',
      },
      'Blade Flourish': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_swords_blade_flourish_attack_addon' },
        ],
        notes: "XGE p.16 — When you take the Attack action, walking speed +10 ft until end of turn; once per turn you can apply a Blade Flourish (Defensive/Slashing/Mobile) to a weapon attack, expending one Bardic Inspiration die. Per-attack toggle consumer; shared resource with Bardic Inspiration.",
      },
      'Extra Attack': {
        minLevel: 6,
        effects: [
          { type: 'extra_attack', operation: 'add', value: 1 },
        ],
        notes: 'XGE p.17 — Attack twice instead of once when taking the Attack action.',
      },
      "Master's Flourish": {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_swords_masters_flourish_d6_substitute' },
        ],
        notes: 'XGE p.17 — When you use a Blade Flourish, you can roll a d6 in place of expending a Bardic Inspiration die. Per-flourish resource-substitution consumer.',
      },
    },

    'College of Whispers': {
      source: 'xanathars',
      'Psychic Blades': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_whispers_psychic_blades_extra_damage' },
        ],
        notes: "XGE p.17 — When you hit a creature with a weapon attack, you can expend one Bardic Inspiration die to deal extra psychic damage equal to 2d6/3d6/5d6/8d6 (by tier). Per-attack damage rider consumer; shared resource with Bardic Inspiration. [BREADCRUMB] Per-attack psychic damage rider.",
      },
      'Words of Terror': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_whispers_words_of_terror_charm_fear' },
        ],
        notes: 'XGE p.17 — 1-minute private conversation with a humanoid: it must succeed a WIS save or be frightened of you or another creature you choose for 1 hour. Once per short or long rest. Per-rest tracker + fear-state consumer.',
      },
      'Mantle of Whispers': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_whispers_mantle_of_whispers_steal_shadow' },
        ],
        notes: "XGE p.17 — Reaction when a humanoid dies within 30 ft: capture its shadow; later, action to don the shadow as a disguise (lasts 1 hour or until ended). Once per short or long rest. Per-rest tracker + disguise-state consumer.",
      },
      'Shadow Lore': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'bard_whispers_shadow_lore_charm_dark_secret' },
        ],
        notes: 'XGE p.17 — Action: whisper to a creature within 30 ft; it must succeed a WIS save or be charmed for 8 hours, willing to obey commands that do not directly endanger it. Once per short or long rest. Per-rest tracker + charm-state consumer.',
      },
    },
  },
  Cleric: {
    'Knowledge Domain': {
      source: 'srd',
      // [PHASE B / #037 — 2026-05-06]: LevelUpWizard step-dispatch.
      //   Knowledge Domain Cleric L1 grants 2 expertise picks via
      //   Blessings of Knowledge (restricted to Arcana / History /
      //   Nature / Religion in the wizard renderer). Pre-#037 this
      //   was a hardcoded `className === 'Cleric' && classLevel === 1
      //   && subclass === 'Knowledge Domain'` branch in the wizard.
      levelProgression: [
        { atLevel: 1, action: 'expertisePicks', count: 2, restrictedTo: ['Arcana', 'History', 'Nature', 'Religion'] },
      ],
      'Blessings of Knowledge': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_knowledge_2_skills_with_expertise' },
        ],
        notes: "PHB p.59 — Two languages + proficiency in two of Arcana/History/Nature/Religion + expertise in those (proficiency bonus doubled). Wired: LevelUpWizard needsExpertisePicks (Cleric L1 + Knowledge Domain branch, restricted to Arcana/History/Nature/Religion) → App.jsx expertisePicks commit (→ playerStats.expertiseSkills; effectRegistry proficiencyLevel:2).",
      },
      'Channel Divinity: Knowledge of the Ages': {
        minLevel: 2,
        effects: [],
        notes: 'PHB p.59 — Channel Divinity: 10 min proficiency in any one skill or tool. Resource via classResources Cleric Channel Divinity. Per-option effect on toggle. [BREADCRUMB] Skill/tool prof toggle on CD activation.',
      },
      'Channel Divinity: Read Thoughts': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.59 — Channel Divinity: read thoughts of a creature within 60 ft for 1 min, can cast Suggestion as part. Action-economy.',
      },
      'Potent Spellcasting': {
        minLevel: 8,
        effects: [
          { type: 'damageBonus', operation: 'add', value: 'wisdom_modifier', condition: { when: 'castingClericCantripDamage' } },
        ],
        // [PHASE B / #035 — 2026-05-06]: Surfaces the
        //   `castingClericCantripDamage` combat toggle whenever this
        //   feature is active (Cleric L8+ on this subclass). Closes
        //   the multi-subclass OR-list anti-pattern in
        //   src/data/combatToggles.js — same field on each Domain
        //   that grants Potent Spellcasting (Knowledge / Light /
        //   Grave / Peace), the helper finds it via property scan.
        surfacesCombatToggle: 'castingClericCantripDamage',
        notes: 'PHB p.59 — Add WIS mod to damage of cleric cantrips. [BREADCRUMB] castingClericCantripDamage flag set by spellCastingService when casting a cleric cantrip with a damage roll. Combat panel + spellCastingService consume in BC ticket.',
      },
      'Visions of the Past': {
        minLevel: 17,
        effects: [],
        notes: 'PHB p.60 — 1 minute meditation: glean info about a creature/object. Narrative — DM-driven.',
      },
    },

    'Life Domain': {
      source: 'srd',
      'Bonus Proficiency': {
        minLevel: 1,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'armor:heavy', proficiencyLevel: 1 },
        ],
        notes: 'PHB p.60 — Heavy armor proficiency.',
      },
      'Disciple of Life': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_life_disciple_heal_bonus' },
        ],
        notes: 'PHB p.60 — Healing spells of 1st level or higher heal additional 2 + spell level HP. Wired via spellCastingService.rollSpellDamage (commit Phase 4b BC-8 fix, APR 2026).',
      },
      'Channel Divinity: Preserve Life': {
        minLevel: 2,
        effects: [],
        notes: 'PHB p.60 — Channel Divinity: heal 5×Cleric level HP distributed among creatures within 30 ft (max half their HP). Resource via classResources Cleric Channel Divinity.',
      },
      'Blessed Healer': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.60 — When you cast a healing spell of 1+ level on others, you regain 2 + spell level HP. [BREADCRUMB] spellCastingService self-heal-on-others-heal hook.',
      },
      'Divine Strike': {
        minLevel: 8,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_life_divine_strike_radiant' },
        ],
        notes: 'PHB p.60 — Once per turn on weapon hit, +1d8 radiant damage (1d8 → 2d8 at L14). Combat-tracker damage rider.',
      },
      'Supreme Healing': {
        minLevel: 17,
        effects: [],
        notes: 'PHB p.60 — Healing spells use maximum dice instead of rolling. [BREADCRUMB] spellCastingService max-dice-on-heal hook.',
      },
    },

    'Light Domain': {
      source: 'srd',
      'Bonus Cantrip': {
        minLevel: 1,
        effects: [],
        notes: 'PHB p.60 — Learn the Light cantrip if you don\'t already know it (does not count against cantrips known). Spell-list grant — wizard handles auto-add.',
      },
      'Warding Flare': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_light_warding_flare_reaction' },
        ],
        notes: 'PHB p.60 — Reaction: impose disadvantage on an attacker within 30 ft. WIS-mod uses per long rest. [BREADCRUMB] Reaction handler + per-rest tracker.',
      },
      'Channel Divinity: Radiance of the Dawn': {
        minLevel: 2,
        effects: [],
        notes: 'PHB p.60 — Channel Divinity: dispel magical darkness; 2d10+Cleric level radiant damage to creatures within 30 ft (CON save half).',
      },
      'Improved Flare': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.60 — Warding Flare can also be triggered when an attack targets a creature you can see. [BREADCRUMB] Warding Flare reaction handler scope expansion.',
      },
      'Potent Spellcasting': {
        minLevel: 8,
        effects: [
          { type: 'damageBonus', operation: 'add', value: 'wisdom_modifier', condition: { when: 'castingClericCantripDamage' } },
        ],
        // [PHASE B / #035 — 2026-05-06]: see Knowledge Domain entry above.
        surfacesCombatToggle: 'castingClericCantripDamage',
        notes: 'PHB p.60 — Add WIS mod to damage of cleric cantrips.',
      },
      'Corona of Light': {
        minLevel: 17,
        effects: [],
        notes: 'PHB p.60 — Action: emit 60 ft sunlight aura for 1 min. Enemies in aura: disadvantage on saves vs spells dealing fire/radiant. [BREADCRUMB] Aura buff/debuff + duration tracking.',
      },
    },

    'Nature Domain': {
      source: 'srd',
      'Acolyte of Nature': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_nature_acolyte_skill_or_cantrip' },
        ],
        notes: 'PHB p.61 — Learn one druid cantrip + proficiency in one of Animal Handling/Nature/Survival. [BREADCRUMB] Wizard offers picker; cantrip add via grantedSpells path.',
      },
      'Bonus Proficiency': {
        minLevel: 1,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'armor:heavy', proficiencyLevel: 1 },
        ],
        notes: 'PHB p.61 — Heavy armor proficiency.',
      },
      'Channel Divinity: Charm Animals and Plants': {
        minLevel: 2,
        effects: [],
        notes: 'PHB p.61 — Channel Divinity: charm beasts/plants within 30 ft (WIS save) for 1 min.',
      },
      'Dampen Elements': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.61 — Reaction: grant resistance to acid/cold/fire/lightning/thunder damage to a creature within 30 ft. [BREADCRUMB] Reaction handler granting resistance to a target.',
      },
      'Divine Strike': {
        minLevel: 8,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_nature_divine_strike_choose_cold_fire_lightning' },
        ],
        notes: 'PHB p.61 — Once per turn on weapon hit, +1d8 cold OR fire OR lightning (chosen at L8). Combat-tracker damage rider.',
      },
      'Master of Nature': {
        minLevel: 17,
        effects: [],
        notes: 'PHB p.61 — Charm Animals and Plants now lets you command those charmed by it as a bonus action. Action-economy.',
      },
    },

    'Tempest Domain': {
      source: 'srd',
      'Bonus Proficiencies': {
        minLevel: 1,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'armor:heavy', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'weapon:martial', proficiencyLevel: 1 },
        ],
        notes: 'PHB p.61 — Heavy armor + martial weapon proficiency.',
      },
      'Wrath of the Storm': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_tempest_wrath_reaction' },
        ],
        notes: 'PHB p.61 — Reaction when hit by attacker within 5 ft: 2d8 lightning OR thunder damage (DEX save half). WIS-mod uses per long rest. [BREADCRUMB] Reaction handler + per-rest tracker.',
      },
      'Channel Divinity: Destructive Wrath': {
        minLevel: 2,
        effects: [],
        notes: 'PHB p.61 — Channel Divinity: maximize next thunder/lightning damage roll. [BREADCRUMB] Damage-roll consumer must check this flag and skip dice rolls.',
      },
      'Thunderbolt Strike': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.61 — When you deal lightning damage to a Large or smaller creature, you can push it 10 ft. Combat-tracker push-effect rider.',
      },
      'Divine Strike': {
        minLevel: 8,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_tempest_divine_strike_thunder' },
        ],
        notes: 'PHB p.61 — Once per turn on weapon hit, +1d8 thunder damage (1d8 → 2d8 at L14). Combat-tracker damage rider.',
      },
      'Stormborn': {
        minLevel: 17,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_tempest_stormborn_fly_outdoors' },
        ],
        notes: 'PHB p.61 — Fly speed = walking speed when not underground or indoors. [BREADCRUMB] Conditional fly speed by environment flag.',
      },
    },

    'Trickery Domain': {
      source: 'srd',
      'Blessing of the Trickster': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_trickery_blessing_advantage_stealth_touch' },
        ],
        notes: 'PHB p.62 — Touch a willing creature: advantage on Stealth checks for 1 hour. [BREADCRUMB] Buff-on-other tracking.',
      },
      'Channel Divinity: Invoke Duplicity': {
        minLevel: 2,
        effects: [],
        notes: 'PHB p.62 — Channel Divinity: action to create an illusory duplicate within 30 ft for 1 min (concentration). [BREADCRUMB] Duplicate-as-token + advantage on attacks while ally.',
      },
      'Channel Divinity: Cloak of Shadows': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.62 — Channel Divinity: become invisible until end of next turn.',
      },
      'Divine Strike': {
        minLevel: 8,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_trickery_divine_strike_poison' },
        ],
        notes: 'PHB p.62 — Once per turn on weapon hit, +1d8 poison damage (1d8 → 2d8 at L14). Combat-tracker damage rider.',
      },
      'Improved Duplicity': {
        minLevel: 17,
        effects: [],
        notes: 'PHB p.62 — Invoke Duplicity creates up to 4 duplicates; bonus action to move any of them up to 30 ft.',
      },
    },

    'War Domain': {
      source: 'srd',
      'Bonus Proficiencies': {
        minLevel: 1,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'armor:heavy', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'weapon:martial', proficiencyLevel: 1 },
        ],
        notes: 'PHB p.62 — Heavy armor + martial weapon proficiency.',
      },
      'War Priest': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_war_priest_bonus_action_attack' },
        ],
        notes: 'PHB p.62 — When you take the Attack action, bonus action: one additional weapon attack. WIS-mod uses per long rest. Resource via classResources Cleric:War Domain. [BREADCRUMB BC-7] Combat panel bonus-action attack consumer.',
      },
      'Channel Divinity: Guided Strike': {
        minLevel: 2,
        effects: [],
        notes: 'PHB p.62 — Channel Divinity: +10 to an attack roll (after seeing roll). [BREADCRUMB] Attack-roll modifier on CD spend.',
      },
      "Channel Divinity: War God's Blessing": {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.62 — Channel Divinity (reaction): when ally within 30 ft attacks, grant +10 to their attack roll. [BREADCRUMB] Reaction handler + ally-targeting.',
      },
      'Divine Strike': {
        minLevel: 8,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_war_divine_strike_weapon' },
        ],
        notes: 'PHB p.62 — Once per turn on weapon hit, +1d8 damage (same type as weapon) (1d8 → 2d8 at L14). Combat-tracker damage rider.',
      },
      'Avatar of Battle': {
        minLevel: 17,
        effects: [
          { type: 'resistance', operation: 'grant', value: 'bludgeoning', damageSource: 'nonmagical' },
          { type: 'resistance', operation: 'grant', value: 'piercing',    damageSource: 'nonmagical' },
          { type: 'resistance', operation: 'grant', value: 'slashing',    damageSource: 'nonmagical' },
        ],
        notes: 'PHB p.62 — Resistance to bludgeoning/piercing/slashing damage from nonmagical weapons. damageSource:nonmagical filters at combat-resolution time. UI continues to show all three pills via getDamageResistances.',
      },
    },

    'Peace Domain': {
      source: 'tashas',
      'Implement of Peace': {
        minLevel: 1,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'skill:insight', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'skill:performance', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'skill:persuasion', proficiencyLevel: 1 },
        ],
        notes: 'TCE p.32 — Proficiency in one of Insight, Performance, or Persuasion. Player picks via wizard; we register all 3 as candidates. [BREADCRUMB] Wizard offers picker.',
      },
      'Emboldening Bond': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_peace_emboldening_bond' },
        ],
        notes: 'TCE p.32 — Action: bond up to PB creatures (within 30 ft of each other) for 10 min. Bonded creatures within 30 ft of each other can add 1d4 to attack/check/save. [BREADCRUMB] Bond tracker + d4 buff on bonded targets.',
      },
      'Channel Divinity: Balm of Peace': {
        minLevel: 2,
        effects: [],
        notes: 'TCE p.32 — Channel Divinity: action — move up to your speed without OAs; heal 2d6+WIS mod each creature you stop within 5 ft of (once per creature).',
      },
      'Protective Bond': {
        minLevel: 6,
        effects: [],
        notes: 'TCE p.32 — Bonded creature can use reaction to teleport up to 30 ft to take a hit for an ally also bonded. [BREADCRUMB] Reaction-teleport-tank handler.',
      },
      'Potent Spellcasting': {
        minLevel: 8,
        effects: [
          { type: 'damageBonus', operation: 'add', value: 'wisdom_modifier', condition: { when: 'castingClericCantripDamage' } },
        ],
        // [PHASE B / #035 — 2026-05-06]: see Knowledge Domain entry above.
        surfacesCombatToggle: 'castingClericCantripDamage',
        notes: 'TCE p.32 — Add WIS mod to damage of cleric cantrips.',
      },
      'Expansive Bond': {
        minLevel: 17,
        effects: [],
        notes: 'TCE p.32 — Emboldening Bond range expands to 60 ft (was 30); bonded creatures can use reaction to halve damage from a hit, sharing the damage.',
      },
    },

    'Twilight Domain': {
      source: 'tashas',
      'Bonus Proficiencies': {
        minLevel: 1,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'armor:heavy', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'weapon:martial', proficiencyLevel: 1 },
        ],
        notes: "TCE p.34 — Heavy armor + martial weapon proficiency.",
      },
      'Eyes of Night': {
        minLevel: 1,
        effects: [
          { type: 'sense', operation: 'grant', target: 'darkvision', value: 300 },
          { type: 'feature', operation: 'grant', value: 'cleric_twilight_eyes_share_darkvision' },
        ],
        notes: 'TCE p.34 — Darkvision out to 300 ft, even through magical darkness; can share with WIS-mod allies for 1 hour as an action. [BREADCRUMB] Action-share-darkvision-aura.',
      },
      'Vigilant Blessing': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_twilight_vigilant_initiative_advantage' },
        ],
        notes: 'TCE p.34 — Action: touch a creature (incl. self) — that creature has advantage on next initiative roll. Single recipient until used. [BREADCRUMB] Advantage-on-initiative buff carry.',
      },
      'Channel Divinity: Twilight Sanctuary': {
        minLevel: 2,
        effects: [],
        notes: 'TCE p.34 — Channel Divinity: action — emit 30 ft aura for 1 min (concentration). Allies entering or starting turn in aura get either 1d6+Cleric level temp HP OR end charmed/frightened. [BREADCRUMB] Aura tick + temp HP / condition end.',
      },
      'Steps of Night': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_twilight_steps_of_night_fly_in_dim_light' },
        ],
        notes: 'TCE p.35 — In dim light or darkness, bonus action to gain fly speed equal to walking speed for 1 min. PB uses per long rest. [BREADCRUMB] Conditional fly + per-rest tracker.',
      },
      'Divine Strike': {
        minLevel: 8,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_twilight_divine_strike_radiant_or_necrotic' },
        ],
        notes: 'TCE p.35 — Once per turn on weapon hit, +1d8 radiant OR necrotic damage (chosen each time). Combat-tracker damage rider.',
      },
      'Twilight Shroud': {
        minLevel: 17,
        effects: [],
        notes: 'TCE p.35 — Allies within Twilight Sanctuary aura have half cover.',
      },
    },

    'Forge Domain': {
      source: 'xanathars',
      'Bonus Proficiencies': {
        minLevel: 1,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'armor:heavy', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'tool:smiths_tools', proficiencyLevel: 1 },
        ],
        notes: "XGE p.18 — Heavy armor + smith's tools proficiency.",
      },
      'Blessing of the Forge': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_forge_blessing_plus_one_armor_or_weapon' },
        ],
        notes: 'XGE p.18 — At end of long rest, touch one nonmagical weapon or armor → +1 magical bonus until next long rest or another item is targeted. [BREADCRUMB] Item-enchant tracker; consumer must apply +1 to AC/attack from blessed item.',
      },
      "Channel Divinity: Artisan's Blessing": {
        minLevel: 2,
        effects: [],
        notes: 'XGE p.19 — Channel Divinity: 1-hour ritual to create a simple metal item or replicate a non-magical metal item ≤100gp. Narrative crafting.',
      },
      'Soul of the Forge': {
        minLevel: 6,
        effects: [
          { type: 'resistance', operation: 'grant', value: 'fire' },
          { type: 'feature', operation: 'grant', value: 'cleric_forge_soul_heavy_armor_plus_one' },
          // [Phase 4b silent-gap fix — APR 2026]: +1 AC while wearing heavy
          //   armor. The wearingHeavyArmor flag is set by effectCalculator
          //   ._buildContext from the equipped armor row, so the AC consumer
          //   filters this effect correctly when the player is in heavy
          //   armor (and skips it otherwise). Was previously a feature flag
          //   only — silent gap caught in next-phase prep audit.
          { type: 'ac', operation: 'add', value: 1, condition: { when: 'wearingHeavyArmor' } },
        ],
        notes: 'XGE p.19 — Resistance to fire damage; +1 AC while wearing heavy armor. AC bonus wired via condition: { when: "wearingHeavyArmor" } (commit Phase 4b silent-gap fix, APR 2026).',
      },
      'Divine Strike': {
        minLevel: 8,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_forge_divine_strike_fire' },
        ],
        notes: 'XGE p.19 — Once per turn on weapon hit, +1d8 fire damage (1d8 → 2d8 at L14). Combat-tracker damage rider.',
      },
      'Saint of Forge and Fire': {
        minLevel: 17,
        effects: [
          { type: 'resistance', operation: 'immunity', value: 'fire' },
          { type: 'resistance', operation: 'grant', value: 'bludgeoning', damageSource: 'nonmagical', condition: { when: 'wearingHeavyArmor' } },
          { type: 'resistance', operation: 'grant', value: 'piercing',    damageSource: 'nonmagical', condition: { when: 'wearingHeavyArmor' } },
          { type: 'resistance', operation: 'grant', value: 'slashing',    damageSource: 'nonmagical', condition: { when: 'wearingHeavyArmor' } },
        ],
        notes: 'XGE p.19 — Immunity to fire damage. While wearing heavy armor, resistance to bludgeoning/piercing/slashing from nonmagical attacks. operation:immunity surfaces fire to getDamageImmunities; B/P/S use damageSource:nonmagical + condition:wearingHeavyArmor.',
      },
    },

    'Grave Domain': {
      source: 'xanathars',
      'Circle of Mortality': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_grave_circle_max_heal_at_zero' },
          { type: 'feature', operation: 'grant', value: 'cleric_grave_spare_the_dying_bonus_action_30ft' },
        ],
        notes: 'XGE p.19 — Healing spells targeting a creature at 0 HP use maximum dice; gain Spare the Dying as cantrip with 30 ft range cast as bonus action. [BREADCRUMB BC-8 adjacent] spellCastingService max-dice-on-zero-target hook + Spare the Dying range/action override.',
      },
      'Eyes of the Grave': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'cleric_grave_eyes_detect_undead' },
        ],
        notes: 'XGE p.19 — Action: 60-ft detect undead creatures (not behind total cover, not protected from divination). PB uses per long rest. [BREADCRUMB] Detect-undead action handler + per-rest tracker.',
      },
      'Channel Divinity: Path to the Grave': {
        minLevel: 2,
        effects: [],
        notes: 'XGE p.19 — Channel Divinity: curse a creature within 30 ft → next attack against it has vulnerability to all damage of that hit. [BREADCRUMB] Combat-tracker first-attack-vulnerability rider.',
      },
      "Sentinel at Death's Door": {
        minLevel: 6,
        effects: [],
        notes: "XGE p.19 — Reaction when a creature within 30 ft takes a crit: turn it into a normal hit. PB uses per long rest. [BREADCRUMB] Reaction handler + per-rest tracker.",
      },
      'Potent Spellcasting': {
        minLevel: 8,
        effects: [
          { type: 'damageBonus', operation: 'add', value: 'wisdom_modifier', condition: { when: 'castingClericCantripDamage' } },
        ],
        // [PHASE B / #035 — 2026-05-06]: see Knowledge Domain entry above.
        surfacesCombatToggle: 'castingClericCantripDamage',
        notes: 'XGE p.19 — Add WIS mod to damage of cleric cantrips.',
      },
      'Keeper of Souls': {
        minLevel: 17,
        effects: [],
        notes: 'XGE p.20 — When an enemy within 60 ft dies, a creature within 60 ft (other than dying one) gains HP equal to dying creature\'s Hit Dice. [BREADCRUMB] Death-trigger heal handler.',
      },
    },
  },
  Druid: {
    'Circle of the Land': {
      source: 'srd',
      'Bonus Cantrip': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_land_bonus_cantrip' },
        ],
        notes: 'PHB p.68 — Learn one additional druid cantrip of your choice. Spell-list grant — wizard handles auto-add. [BREADCRUMB] Cantrip add via grantedSpells path.',
      },
      'Natural Recovery': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_land_natural_recovery' },
        ],
        notes: 'PHB p.68 — On short rest, recover spell slots totaling up to half your druid level (round up); slots must be 5th level or lower. Once per long rest. [BREADCRUMB BC-7] Half-druid-level spell-slot recovery on short rest — RestPanel hook needed.',
      },
      "Land's Stride": {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'druid_lands_stride_difficult_terrain' },
          { type: 'advantage', operation: 'advantage', target: 'save:plant_restraint_magical' },
        ],
        notes: 'PHB p.69 — Move through nonmagical plant difficult terrain at full speed without damage; advantage on saves vs magical plant restraint.',
      },
      "Nature's Ward": {
        minLevel: 10,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_land_natures_ward_immunities' },
        ],
        notes: "PHB p.69 — Can't be charmed or frightened by elementals or fey, and immune to poison and disease. Encoded as feature flag because the registry does not currently distinguish immunity from resistance (mapping table line 1001). [BREADCRUMB] Immunity-vs-resistance distinction.",
      },
      "Nature's Sanctuary": {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_land_natures_sanctuary' },
        ],
        notes: 'PHB p.69 — Beasts and plants must succeed on a WIS save or be unable to attack you for that turn (save DC = 8 + prof + WIS). [BREADCRUMB] Save-DC + per-attack target gating.',
      },
    },

    'Circle of the Moon': {
      source: 'srd',
      'Combat Wild Shape': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_moon_combat_wild_shape' },
        ],
        notes: 'PHB p.69 — Use Wild Shape as a bonus action; while wild shaped you may spend a spell slot as a bonus action to regain 1d8 HP per slot level. Action-economy + heal handler. [BREADCRUMB] Bonus-action wild shape + slot-to-heal hook.',
      },
      'Circle Forms': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_moon_circle_forms_cr_relaxed' },
        ],
        notes: 'PHB p.69 — At L2 wild shape into beasts of CR 1; CR limit increases to your druid level / 3 (round down) at L6. Wild Shape CR limit consumer reads this flag.',
      },
      'Primal Strike': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_moon_primal_strike_magical_attacks' },
        ],
        notes: 'PHB p.69 — While wild shaped, your attacks count as magical for resistance/immunity bypass. [BREADCRUMB] Magic-attack flag for combat resolution.',
      },
      'Elemental Wild Shape': {
        minLevel: 10,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_moon_elemental_wild_shape' },
        ],
        notes: 'PHB p.69 — Spend two uses of Wild Shape (one action) to transform into an air, earth, fire, or water elemental. Action-economy + form options. [BREADCRUMB] Elemental form picker.',
      },
      'Thousand Forms': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_moon_thousand_forms_alter_self_at_will' },
          // [Phase 4b BC-13 fix — APR 2026]: Wire Alter Self as a true at-will
          //   grant so SpellsPanel renders ∞ uses and spellCastingService skips
          //   slot consumption. The feature flag stays for breadcrumb-style
          //   queries (e.g., NPC dialogue / narrative checks).
          { type: 'atWillSpell', operation: 'grant', value: 'Alter Self' },
        ],
        notes: 'PHB p.69 — Cast Alter Self at will. Wired via atWillSpell effect (commit Phase 4b BC-13 fix, APR 2026).',
      },
    },

    // [SOURCES]: Available when Tasha's is enabled
    'Circle of Stars': {
      source: 'tashas',
      // [PHASE B / #037 — 2026-05-06]: LevelUpWizard step-dispatch.
      //   Druid Circle of the Stars L2 picks an initial Starry Form
      //   constellation (Archer / Chalice / Dragon) — stored at
      //   subclassData.starryForm. Pre-#037 was a hardcoded branch
      //   in the wizard's needsSubclassChoices block.
      levelProgression: [
        { atLevel: 2, action: 'subclassPick', subclassDataKey: 'starryForm', dialogTitle: 'Starry Form',
          headerEmoji: '✨',
          subtitle: 'Pick a starting constellation. You can swap on activation each round at L10 (Twinkling Constellations).',
          options: [
            { key: 'Archer',  icon: '🏹', description: 'Radiant ranged spell attack at 60 ft for 1d8+WIS damage as bonus action while in form (1d10 at L10).' },
            { key: 'Chalice', icon: '🍷', description: 'When you cast a spell that restores HP, you or another creature within 30 ft also regains 1d8+WIS HP (1d10 at L10).' },
            { key: 'Dragon',  icon: '🐉', description: 'When you make an INT/WIS check or concentration save, treat a d20 of 9 or lower as 10. At L10, 20 ft fly speed (hover).' },
          ],
        },
      ],
      'Star Map': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_stars_star_map' },
          // [Phase 4b BC-13 follow-up — APR 2026]: 1/long-rest free Guiding
          //   Bolt cast wired via the perRestSpell consumer. SpellsPanel +
          //   spellCastingService can now check for an available free use
          //   instead of consuming a slot.
          { type: 'perRestSpell', operation: 'grant', value: 'Guiding Bolt', recharge: 'long_rest', uses: 1 },
        ],
        notes: 'TCE p.37 — Carry a star map; know Guidance cantrip and have Guiding Bolt prepared (does not count vs prepared). Cast Guiding Bolt without a slot once per long rest, plus higher-level slots. Free cast wired via perRestSpell effect.',
      },
      'Starry Form': {
        minLevel: 2,
        // [Phase 4b bucket 3 followup — APR 2026]: Promoted from a single feature
        // flag to a choices block. The active constellation (selected each time the
        // form is invoked) is stored at classEntry.subclassData.starryForm.
        // The base feature flag still registers (combat panel uses it to know the
        // form is available); the per-constellation flag layers on top when the
        // form is active.
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_stars_starry_form_available' },
        ],
        choiceKey: 'starryForm',
        choices: {
          'Archer': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'circle_stars_archer_form' },
            ],
            notes: 'TCE p.37 — Archer: radiant ranged spell attack at 60 ft for 1d8+WIS damage as bonus action while in form (1d10 at L10).',
          },
          'Chalice': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'circle_stars_chalice_form' },
            ],
            notes: 'TCE p.37 — Chalice: when you cast a spell that restores HP, you or another creature within 30 ft also regains 1d8+WIS HP (1d10 at L10).',
          },
          'Dragon': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'circle_stars_dragon_form' },
            ],
            notes: 'TCE p.37 — Dragon: when you make an INT/WIS check or concentration save, treat a d20 roll of 9 or lower as 10. At L10, gain 20 ft fly speed (hover).',
          },
        },
        notes: "TCE p.37 — Bonus action: spend a Wild Shape use to assume a Starry Form (Archer, Chalice, or Dragon constellation), 10 min duration. Constellation choice stored at subclassData.starryForm. Wired: LevelUpWizard needsSubclassChoices (Druid Circle of Stars L2) subclassChoices.starryForm picker → App.jsx subclassChoices.starryForm commit (→ classes[i].subclassData.starryForm). Twinkling Constellations re-pick is activation-time.",
      },
      'Cosmic Omen': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_stars_cosmic_omen' },
        ],
        notes: 'TCE p.38 — After a long rest, roll a d6: even = Weal (add d6 to attack rolls / saves of others), odd = Woe (subtract). Per-rest reaction uses = prof bonus. [BREADCRUMB] Per-rest die roll + reaction tracker.',
      },
      'Twinkling Constellations': {
        minLevel: 10,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_stars_twinkling_constellations' },
        ],
        notes: 'TCE p.38 — Archer/Chalice damage and healing dice become d8; Dragon grants 20 ft fly speed (hover). At start of each turn in Starry Form, may switch constellation. [BREADCRUMB] Per-form upgrade + fly speed grant.',
      },
      'Full of Stars': {
        minLevel: 14,
        effects: [
          { type: 'resistance', operation: 'grant', value: 'bludgeoning', condition: { when: 'inStarryForm' } },
          { type: 'resistance', operation: 'grant', value: 'piercing',    condition: { when: 'inStarryForm' } },
          { type: 'resistance', operation: 'grant', value: 'slashing',    condition: { when: 'inStarryForm' } },
        ],
        notes: 'TCE p.38 — While in Starry Form, you become partially incorporeal — resistance to bludgeoning, piercing, and slashing damage. [BREADCRUMB] inStarryForm combat-toggle flag — combat panel must set when Starry Form active.',
      },
    },

    'Circle of Wildfire': {
      source: 'tashas',
      'Circle Spells': {
        minLevel: 2,
        effects: [],
        notes: 'TCE p.38 — Always-prepared circle spells (Burning Hands, Cure Wounds at L3; Flaming Sphere, Scorching Ray at L5; etc.). Spell-list expansion — already handled by effectCalculator.getSubclassSpells (mapping table line 1009).',
      },
      'Summon Wildfire Spirit': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_wildfire_summon_spirit' },
        ],
        notes: 'TCE p.38 — Spend a Wild Shape use (action, 1 hr duration) to summon a wildfire spirit ally (Flame Seed teleport, Fiery Teleportation aura). [BREADCRUMB] Summon stat-block + ally-token tracking.',
      },
      'Enhanced Bond': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_wildfire_enhanced_bond' },
        ],
        notes: 'TCE p.38 — Fire/healing spells get +1d8; can teleport to spirit (15 ft); spirit benefits from your single-target spells. [BREADCRUMB] Spell damage/heal rider + summon-targeting hook.',
      },
      'Cauterizing Flames': {
        minLevel: 10,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_wildfire_cauterizing_flames' },
        ],
        notes: 'TCE p.38 — When a creature dies within 30 ft, a flame springs up there for 1 minute; reaction 2d10+druid level fire damage OR heal. [BREADCRUMB] Death-trigger zone marker + reaction handler.',
      },
      'Blazing Revival': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_wildfire_blazing_revival' },
        ],
        notes: 'TCE p.38 — When you would drop to 0 HP while spirit is summoned, reaction: dismiss spirit and revive at half HP instead. Once per long rest. [BREADCRUMB] HP-zero reaction trigger + per-rest tracker.',
      },
    },

    // [SOURCES]: Available when Xanathar's is enabled
    'Circle of Dreams': {
      source: 'xanathars',
      'Balm of the Summer Court': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_dreams_balm_of_summer_court' },
        ],
        notes: 'XGE p.23 — Pool of d6 healing dice = druid level (refreshes long rest). Bonus action: heal an ally within 120 ft and grant temp HP. [BREADCRUMB] Per-day d6 pool resource (classResources entry).',
      },
      'Hearth of Moonlight and Shadow': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_dreams_hearth_moonlight_shadow' },
        ],
        notes: 'XGE p.23 — During short/long rest, create a 30-ft sphere of light/shadow giving total cover from outside; grants +5 stealth and perception bonuses to everyone inside. [BREADCRUMB] Rest-time aura + stealth-grant.',
      },
      'Hidden Paths': {
        minLevel: 10,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_dreams_hidden_paths' },
        ],
        notes: 'XGE p.23 — Bonus action: teleport up to 60 ft to a space you can see (or teleport a willing creature up to 30 ft). WIS-mod uses per long rest. [BREADCRUMB] Per-rest teleport action + uses tracker.',
      },
      'Walker in Dreams': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_dreams_walker_in_dreams' },
          // [Phase 4b BC-13 follow-up — APR 2026]: 1/long-rest free casts of
          //   Dream, Scrying, OR Teleportation Circle. The player picks one
          //   per rest cycle. Wired as three independent perRestSpell entries
          //   sharing the same recharge — the consumer surfaces all three
          //   with available uses, and casting one decrements just that one.
          //   PHB-style "pick one of three" is encoded by giving each its own
          //   tracker; players can technically use multiple in the same rest
          //   cycle (a permissive interpretation). Strict 1/rest among the
          //   three would need a shared-pool consumer — separate ticket.
          { type: 'perRestSpell', operation: 'grant', value: 'Dream', recharge: 'long_rest', uses: 1 },
          { type: 'perRestSpell', operation: 'grant', value: 'Scrying', recharge: 'long_rest', uses: 1 },
          { type: 'perRestSpell', operation: 'grant', value: 'Teleportation Circle', recharge: 'long_rest', uses: 1 },
        ],
        notes: 'XGE p.23 — At end of short rest, cast Dream / Scrying / Teleportation Circle (target known location) without a slot. Once per long rest. Wired via perRestSpell as three independent free casts. Strict-RAW "pick one of three per rest" is a permissive trade-off — see effect comment.',
      },
    },

    'Circle of the Shepherd': {
      source: 'xanathars',
      // [PHASE B / #037 — 2026-05-06]: LevelUpWizard step-dispatch.
      //   Druid Circle of the Shepherd L2 picks an initial Spirit
      //   Totem (Bear / Hawk / Unicorn) — stored at
      //   subclassData.spiritTotem.
      levelProgression: [
        { atLevel: 2, action: 'subclassPick', subclassDataKey: 'spiritTotem', dialogTitle: 'Spirit Totem',
          headerEmoji: '🌟',
          subtitle: 'Pick a starting Spirit Totem. You can re-pick each time you summon (each is a fresh 60-ft aura, 1-min duration).',
          options: [
            { key: 'Bear',    icon: '🐻', description: 'Bear Spirit aura (60 ft): allies in aura start their turn with 5 + druid level temp HP and have advantage on STR checks/saves while within.' },
            { key: 'Hawk',    icon: '🦅', description: 'Hawk Spirit aura (60 ft): reaction to grant an ally advantage on an attack roll within aura.' },
            { key: 'Unicorn', icon: '🦄', description: 'Unicorn Spirit aura (60 ft): you and allies have advantage on detect-within-1-mile checks. Healing spell rider grants 1 creature 1d6+druid level HP.' },
          ],
        },
      ],
      'Speech of the Woods': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_shepherd_speech_of_the_woods' },
        ],
        notes: 'XGE p.23 — Learn Sylvan; can converse with beasts as if they understand you. Druidic-like animal speech. [BREADCRUMB] Language grant + beast-speech narrative flag.',
      },
      'Spirit Totem': {
        minLevel: 2,
        // [Phase 4b bucket 3 followup — APR 2026]: Promoted from a single feature
        // flag to a choices block. The active spirit totem (selected each time the
        // ability is invoked) is stored at classEntry.subclassData.spiritTotem.
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_shepherd_spirit_totem_available' },
        ],
        // [PHASE B / #036 — 2026-05-06]: PartyPanel aura data block.
        //   State-gated: requiresStateAt walks `subclassData.spiritTotem`,
        //   the chosen totem name picks the description from
        //   descriptionByState. Pre-#036 PartyPanel.js had a hardcoded
        //   `className === 'Druid' && subclass === 'Circle of the Shepherd'`
        //   block at L127.
        aura: {
          requiresStateAt: 'subclassData.spiritTotem',
          nameTemplate: 'Spirit Totem (${state})',
          radius: 60,
          descriptionByState: {
            Bear: 'Allies in 60 ft aura: 5 + druid level temp HP at start of turn + advantage on STR. (XGE p.23)',
            Hawk: 'Reaction to grant ally advantage on attack within 60 ft aura. (XGE p.23)',
            Unicorn: 'Allies advantage on detect within 1 mile + heal-rider on healing spells in 60 ft. (XGE p.23)',
          },
          color: 'bg-emerald-900/30 border-emerald-700/40 text-emerald-200',
        },
        choiceKey: 'spiritTotem',
        choices: {
          'Bear': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'circle_shepherd_bear_spirit_aura' },
            ],
            notes: 'XGE p.23 — Bear Spirit aura (60 ft): allies in aura start their turn with 5 + druid level temp HP and have advantage on STR checks/saves while within. [BREADCRUMB] Per-turn temp HP grant within aura — PartyPanel ally tracker.',
          },
          'Hawk': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'circle_shepherd_hawk_spirit_aura' },
            ],
            notes: 'XGE p.23 — Hawk Spirit aura (60 ft): reaction to grant an ally advantage on an attack roll within aura. [BREADCRUMB] Reaction handler with ally targeting.',
          },
          'Unicorn': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'circle_shepherd_unicorn_spirit_aura' },
            ],
            notes: 'XGE p.23 — Unicorn Spirit aura (60 ft): you and allies in aura have advantage on detect-within-1-mile checks. When you cast a healing spell of L1+, you can also grant 1 creature in aura HP equal to 1d6+druid level. [BREADCRUMB] Detect-within-1-mile narrative + heal-rider on healing spell cast.',
          },
        },
        notes: "XGE p.23 — Bonus action: summon a Bear, Hawk, or Unicorn spirit (60 ft, 1 min duration). Totem choice stored at subclassData.spiritTotem. Wired: LevelUpWizard needsSubclassChoices (Druid Circle of the Shepherd L2) subclassChoices.spiritTotem picker → App.jsx subclassChoices.spiritTotem commit (→ classes[i].subclassData.spiritTotem). Per-activation re-pick and PartyPanel aura display are separate.",
      },
      'Mighty Summoner': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_shepherd_mighty_summoner' },
        ],
        notes: 'XGE p.24 — Beasts and fey summoned/created by your spells gain +2 HP per Hit Die and their attacks count as magical. [BREADCRUMB] Summon HP rider + magic-attack flag on summons.',
      },
      'Guardian Spirit': {
        minLevel: 10,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_shepherd_guardian_spirit' },
        ],
        notes: 'XGE p.24 — Beasts and fey summoned by you within Spirit Totem aura at start of their turn gain temp HP equal to half your druid level. [BREADCRUMB] Per-turn temp-HP grant tied to Spirit Totem aura.',
      },
      'Faithful Summons': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'circle_shepherd_faithful_summons' },
        ],
        notes: 'XGE p.24 — When reduced to 0 HP or knocked unconscious, automatically cast Conjure Animals (4 CR 2 beasts) without a slot. Once per long rest. [BREADCRUMB] HP-zero / unconscious trigger + per-rest tracker.',
      },
    },
  },
  Fighter: {
    'Champion': {
      source: 'srd',
      'Improved Critical': {
        minLevel: 3,
        effects: [
          { type: 'criticalRange', operation: 'min', value: 19 },
        ],
        notes: 'PHB p.72 — Weapon attacks crit on a roll of 19 or 20.',
      },
      'Remarkable Athlete': {
        minLevel: 7,
        effects: [],
        notes: 'PHB p.72 — Add half proficiency bonus (round up) to STR/DEX/CON checks not already proficient. Long jump distance += STR mod. [BREADCRUMB] Half-prof formula needs proficiency_bonus*0.5 with a "non-proficient" target filter on skill consumer — current skill registry consumer applies grants regardless of existing proficiency. Tracked as BC follow-up; spec Section 9.',
      },
      'Additional Fighting Style': {
        minLevel: 10,
        effects: [],
        notes: 'PHB p.72 — Choose a second Fighting Style. Player picks via wizard; effect registered through the existing fighting-style mechanism (not subclass registry).',
      },
      'Superior Critical': {
        minLevel: 15,
        effects: [
          { type: 'criticalRange', operation: 'min', value: 18 },
        ],
        notes: 'PHB p.72 — Weapon attacks crit on 18-20. Stacks with Improved Critical via Math.min (lower wins).',
      },
      'Survivor': {
        minLevel: 18,
        effects: [],
        notes: 'PHB p.72 — At start of turn, regen 5+CON HP if below half HP. [BREADCRUMB BC-4] Needs turn-tick handler not yet built.',
      },
    },

    'Battle Master': {
      source: 'srd',
      'Combat Superiority': {
        minLevel: 3,
        // [Phase B / #043 — MAY 2026]: Superiority die scaling moved to a
        //   data block on the owning feature. Pre-fix, the d8/d10/d12
        //   ladder lived in three places: src/utils/classDieScaling.js,
        //   an inline copy in src/components/DiceTab.js, and
        //   src/data/classResources.js Fighter:Battle Master.dieSize. The
        //   subclass data block is now THE source of truth — the helper
        //   reads it via resolveByLevelTable; classResources keeps its
        //   own dieSize map for ClassResourcesPanel rendering and a
        //   drift-guard test holds the two in sync.
        // PHB p.74 — d8 (L3), d10 (L10), d12 (L18).
        superiorityDieByLevel: [
          { atLevel: 3, value: '1d8' },
          { atLevel: 10, value: '1d10' },
          { atLevel: 18, value: '1d12' },
        ],
        effects: [],
        notes: 'PHB p.73 — Maneuvers + superiority dice. Die size scales by Fighter level (see superiorityDieByLevel data block). Resource tracked via classResources.js Fighter:Battle Master entry. Per-maneuver effects in srdSubclassOptions BATTLE_MASTER_MANEUVERS. Bucket 3 does not register per-maneuver effects (player-toggle level — BC-11).',
      },
      'Student of War': {
        minLevel: 3,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'tool:artisan_player_choice', proficiencyLevel: 1 },
        ],
        notes: "PHB p.73 — Proficiency in one type of artisan's tools. Player chooses; we register a generic proficiency grant. [BREADCRUMB] Tool-choice picker UI is a wizard concern, not subclass effect.",
      },
      'Know Your Enemy': {
        minLevel: 7,
        effects: [],
        notes: 'PHB p.73 — Spend 1 minute observing/interacting to learn target relative-stat info. Narrative — DM-driven.',
      },
      'Improved Combat Superiority': {
        minLevel: 10,
        effects: [],
        notes: 'PHB p.73 — Superiority dice scale to d10 at L10, d12 at L18. Handled via classResources Fighter:Battle Master dieSize table.',
      },
      'Relentless': {
        minLevel: 15,
        effects: [],
        notes: 'PHB p.73 — Regain one superiority die at start of each combat. [BREADCRUMB] Combat-start trigger handler.',
      },
    },

    'Eldritch Knight': {
      source: 'srd',
      'Spellcasting': {
        minLevel: 3,
        effects: [],
        notes: 'PHB p.74 — Third-caster (INT). Already wired via useClassEffects spell-slot calc + getCasterType subclass-aware path.',
      },
      'Weapon Bond': {
        minLevel: 3,
        effects: [],
        notes: 'PHB p.75 — Bond up to 2 weapons; cannot be disarmed; summon as bonus action. Narrative + action-economy.',
      },
      'War Magic': {
        minLevel: 7,
        effects: [],
        notes: 'PHB p.75 — Cast a cantrip in place of one weapon attack. Action-economy — combat panel ticket.',
      },
      'Eldritch Strike': {
        minLevel: 10,
        effects: [],
        notes: 'PHB p.75 — On weapon hit, target has disadvantage on next save vs your spells (until end of next turn). Combat-tracker ticket.',
      },
      'Arcane Charge': {
        minLevel: 15,
        effects: [],
        notes: 'PHB p.75 — Action Surge teleports up to 30 ft before/after.',
      },
      'Improved War Magic': {
        minLevel: 18,
        effects: [],
        notes: 'PHB p.75 — War Magic now allows cantrip + level-1+ spell.',
      },
    },

    'Psi Warrior': {
      source: 'tashas',
      'Psionic Power': {
        minLevel: 3,
        effects: [],
        notes: "TCE p.42 — Psionic Energy dice resource (DC = 8 + INT mod + prof). Resource handled via classResources Fighter:Psi Warrior — verify entry exists; if not, add in this commit.",
      },
      'Telekinetic Adept': {
        minLevel: 7,
        effects: [],
        notes: 'TCE p.42 — Psi-Powered Leap + Telekinetic Thrust (push). Narrative/action.',
      },
      'Guarded Mind': {
        minLevel: 10,
        effects: [
          { type: 'resistance', operation: 'grant', value: 'psychic' },
          { type: 'feature', operation: 'grant', value: 'psi_warrior_guarded_mind_charm_fear_end' },
        ],
        notes: 'TCE p.43 — Resistance to psychic damage; spend 1 psionic die to end charmed/frightened on yourself.',
      },
      'Bulwark of Force': {
        minLevel: 15,
        effects: [],
        notes: 'TCE p.43 — Half-cover for self + 5 allies for 1 minute (concentration).',
      },
      'Telekinetic Master': {
        minLevel: 18,
        effects: [
          // [Phase 4b silent-gap fix — APR 2026]: Telekinesis at-will. Wired
          //   via the existing atWillSpell consumer (effectCalculator.is
          //   SpellAtWill / spellCastingService isAtWill bypass). RAW also
          //   says "without concentration" — that part is mechanically
          //   represented by the atWillSpell grant since concentrationSystem
          //   isn't auto-started for at-will casts.
          { type: 'atWillSpell', operation: 'grant', value: 'Telekinesis' },
        ],
        notes: 'TCE p.43 — Cast Telekinesis at-will without concentration. Wired via atWillSpell effect.',
      },
    },

    'Rune Knight': {
      source: 'tashas',
      'Bonus Proficiencies': {
        minLevel: 3,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'tool:smiths_tools', proficiencyLevel: 1 },
        ],
        notes: "TCE p.46 — Smith's tools proficiency.",
      },
      'Rune Carver': {
        minLevel: 3,
        effects: [],
        notes: 'TCE p.47 — Choose runes (Cloud, Fire, Frost, Stone). Each rune grants passive + active effect. [BREADCRUMB] Rune-choice picker UI + per-rune effect registration as future commit.',
      },
      "Giant's Might": {
        minLevel: 3,
        effects: [],
        notes: "TCE p.46 — Bonus action: become Large, advantage on STR checks/saves, +1d6 damage. Action-economy + temp-buff toggle.",
      },
      'Runic Shield': {
        minLevel: 7,
        effects: [],
        notes: 'TCE p.47 — Reaction: ally within 60 ft has attack against them re-rolled. Reaction handler.',
      },
      'Great Stature': {
        minLevel: 10,
        effects: [],
        notes: 'TCE p.47 — Permanent height increase + d8 instead of d6 on Giant\'s Might damage. [BREADCRUMB] Modify the Giant\'s Might toggle when active.',
      },
      'Master of Runes': {
        minLevel: 15,
        effects: [],
        notes: 'TCE p.47 — Use each rune twice between rests (was once).',
      },
      'Runic Juggernaut': {
        minLevel: 18,
        effects: [],
        notes: "TCE p.47 — Giant's Might becomes Huge + reach +5 ft.",
      },
    },

    'Arcane Archer': {
      source: 'xanathars',
      'Arcane Archer Lore': {
        minLevel: 3,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'skill:arcana', proficiencyLevel: 1 },
        ],
        notes: "XGE p.28 — Choose Arcana or Nature proficiency, plus Prestidigitation or Druidcraft. Defaults to Arcana for registry; player choice via wizard. [BREADCRUMB] Wizard offers both options.",
      },
      'Arcane Shot': {
        minLevel: 3,
        effects: [],
        notes: 'XGE p.28 — Choose 2 Arcane Shot options; spend a use on a shot to add the effect. Per-shot effects in a separate options registry (parallel to Battle Master maneuvers). [BREADCRUMB] Arcane Shot effect registry as follow-up; resource via classResources.',
      },
      'Magic Arrow': {
        minLevel: 7,
        effects: [],
        notes: 'XGE p.29 — Nonmagical ammunition counts as magical for resistance/immunity. [BREADCRUMB] Combat panel attack-resolution upgrades.',
      },
      'Curving Shot': {
        minLevel: 7,
        effects: [],
        notes: 'XGE p.29 — Bonus action to redirect missed magic-arrow attack to another target within 60 ft.',
      },
      'Arcane Shot (2 options)': {
        minLevel: 10,
        effects: [],
        notes: 'XGE p.29 — Learn 1 additional Arcane Shot option (total 3). Resource increase only; no new effect type.',
      },
      'Ever-Ready Shot': {
        minLevel: 15,
        effects: [],
        notes: 'XGE p.29 — Regain expended Arcane Shot use on initiative roll (if 0 remaining). Combat-start trigger.',
      },
      'Arcane Shot (3 options)': {
        minLevel: 18,
        effects: [],
        notes: 'XGE p.29 — Learn 1 additional Arcane Shot option (total 4).',
      },
    },

    'Cavalier': {
      source: 'xanathars',
      'Bonus Proficiency': {
        minLevel: 3,
        effects: [],
        notes: "XGE p.29 — Choose one skill from a list, OR a language. Player picks via wizard; effect registered through standard skill/language grant. [BREADCRUMB] Wizard offers options.",
      },
      'Born to the Saddle': {
        minLevel: 3,
        effects: [
          { type: 'advantage', operation: 'advantage', target: 'save:fall_from_mount' },
        ],
        notes: 'XGE p.30 — Advantage on saves to remain mounted; mount/dismount costs 5 ft (not half speed); land safely from 10 ft fall while mounted.',
      },
      'Unwavering Mark': {
        minLevel: 3,
        effects: [],
        notes: 'XGE p.30 — On melee hit with weapon, mark target until end of your next turn. Marked targets: disadvantage attacking anyone but you, you get a special bonus attack vs them. Combat-tracker territory — BREADCRUMB.',
      },
      'Warding Maneuver': {
        minLevel: 7,
        effects: [],
        notes: 'XGE p.30 — Reaction: ally hit by attack, roll d8, add to AC; if attack misses, ally has resistance to that damage. Per-rest = CON mod uses. Reaction handler.',
      },
      'Hold the Line': {
        minLevel: 10,
        effects: [],
        notes: 'XGE p.30 — Creatures provoke OAs when leaving your reach OR moving 5+ ft within reach. OA on hit reduces speed to 0. Reach + tactical positioning — combat panel.',
      },
      'Ferocious Charger': {
        minLevel: 15,
        effects: [],
        notes: 'XGE p.30 — Move 10+ ft in straight line + attack same turn → target makes STR save or knocked prone. Once per turn. Combat-tracker.',
      },
      'Vigilant Defender': {
        minLevel: 18,
        effects: [],
        notes: 'XGE p.30 — Take a reaction OA every turn round (not just yours), one per other creature.',
      },
    },

    'Samurai': {
      source: 'xanathars',
      // [PHASE B / #037 — 2026-05-06]: LevelUpWizard step-dispatch.
      //   Fighter Samurai L7 Elegant Courtier OR-choice (WIS save
      //   proficiency vs +Persuasion proficiency) — stored at
      //   subclassData.elegantCourtierChoice.
      levelProgression: [
        { atLevel: 7, action: 'subclassPick', subclassDataKey: 'elegantCourtierChoice', dialogTitle: 'Elegant Courtier',
          headerEmoji: '⚔️',
          subtitle: 'You already gain your Wisdom modifier as a bonus to Persuasion checks. Choose one additional proficiency — a permanent character choice.',
          options: [
            { key: 'wis_save',    icon: '🧠', label: 'Wisdom Saving Throw',  description: 'Gain proficiency in Wisdom saving throws.' },
            { key: 'insight',     icon: '👁️', label: 'Insight (skill)',      description: 'Gain proficiency in the Insight skill.' },
            { key: 'performance', icon: '🎭', label: 'Performance (skill)',  description: 'Gain proficiency in the Performance skill.' },
            { key: 'persuasion',  icon: '💬', label: 'Persuasion (skill)',   description: 'Gain proficiency in the Persuasion skill (best synergy with the WIS bonus to Persuasion).' },
          ],
        },
      ],
      'Bonus Proficiency': {
        minLevel: 3,
        effects: [],
        notes: 'XGE p.31 — Proficiency in one of Diplomacy/History/Insight/Performance/Persuasion OR fluency in one language. Player picks via wizard. [BREADCRUMB] Wizard offers options.',
      },
      'Fighting Spirit': {
        minLevel: 3,
        effects: [],
        notes: 'XGE p.31 — Bonus action: 5/10/15 temp HP + advantage on weapon attacks until end of turn. Per-rest = uses scale (3/4/4 by tier). Resource via classResources Fighter:Samurai (add in this commit).',
      },
      'Elegant Courtier': {
        minLevel: 7,
        effects: [
          // Always-on: add WIS modifier to Persuasion checks (XGE p.32)
          { type: 'skill', operation: 'add', target: 'persuasion', value: 'wisdom_modifier' },
        ],
        // [Phase 5 wizard-picks — APR 2026]: Player chooses WIS save proficiency OR
        // one skill proficiency (Insight / Performance / Persuasion). Choice stored at
        // classes[i].subclassData.elegantCourtierChoice; wizard captures via
        // levelUpData.subclassChoices.elegantCourtierChoice.
        choiceKey: 'elegantCourtierChoice',
        choices: {
          wis_save: {
            effects: [
              { type: 'proficiency', operation: 'grant', value: 'save:wisdom', proficiencyLevel: 1 },
            ],
          },
          insight: {
            effects: [
              { type: 'proficiency', operation: 'grant', value: 'skill:insight', proficiencyLevel: 1 },
            ],
          },
          performance: {
            effects: [
              { type: 'proficiency', operation: 'grant', value: 'skill:performance', proficiencyLevel: 1 },
            ],
          },
          persuasion: {
            effects: [
              { type: 'proficiency', operation: 'grant', value: 'skill:persuasion', proficiencyLevel: 1 },
            ],
          },
        },
        notes: "XGE p.32 — Whenever you make a Charisma (Persuasion) check, add your Wisdom modifier (always-on effect). PLUS: gain proficiency in WIS saves OR one of Insight/Performance/Persuasion (player choice, wizard step). Choice stored at subclassData.elegantCourtierChoice; registered via choice-dependent effect block.",
      },
      'Tireless Spirit': {
        minLevel: 10,
        effects: [],
        notes: 'XGE p.32 — Initiative roll regains an expended Fighting Spirit use (if 0 remaining).',
      },
      'Rapid Strike': {
        minLevel: 15,
        effects: [],
        notes: 'XGE p.32 — Trade advantage on attack for one extra weapon attack. Combat-tracker.',
      },
      'Strength Before Death': {
        minLevel: 18,
        effects: [],
        notes: 'XGE p.32 — Reduced to 0 HP: react to take a turn (full action+bonus+move) before falling. Reaction handler.',
      },
    },
  },
  Monk: {
    'Way of the Open Hand': {
      source: 'srd',
      'Open Hand Technique': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_open_hand_technique' },
        ],
        notes: 'PHB p.80 — On Flurry of Blows hit, choose: prone, push 15ft, or no reactions until end of next turn. [BREADCRUMB] Combat-tracker rider on Flurry of Blows attacks.',
      },
      'Wholeness of Body': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_open_hand_wholeness_of_body' },
        ],
        notes: 'PHB p.80 — Action: heal 3*monk level HP, 1/long rest. [BREADCRUMB] Self-heal action handler + per-rest tracker.',
      },
      'Tranquility': {
        minLevel: 11,
        effects: [],
        notes: 'PHB p.80 — At end of long rest, gain Sanctuary effect on self until end of next long rest or you attack. [BREADCRUMB] Buff-on-rest handler.',
      },
      'Quivering Palm': {
        minLevel: 17,
        effects: [],
        notes: 'PHB p.80 — Action: spend 3 ki on unarmed strike to set vibration; later bonus action to end → 10d10 necrotic (CON save half), or 0 HP if save fails. [BREADCRUMB] Combat-tracker delayed-trigger.',
      },
    },

    'Way of Shadow': {
      source: 'srd',
      'Shadow Arts': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_shadow_arts_ki_spells' },
          // Minor Illusion at-will (Phase 4b BC-13 fix, APR 2026).
          { type: 'atWillSpell', operation: 'grant', value: 'Minor Illusion' },
          // [Phase 4b BC-13 closeout — APR 2026]: 2-ki casts wired via
          //   resourceCostSpell. spellCastingService spends 2 ki_points on
          //   cast (skips slot consumption). SpellbookTab shows availability
          //   based on current ki; cast button disabled when ki < 2.
          { type: 'resourceCostSpell', operation: 'grant', value: 'Darkness',           cost: 2, resource: 'ki_points' },
          { type: 'resourceCostSpell', operation: 'grant', value: 'Darkvision',         cost: 2, resource: 'ki_points' },
          { type: 'resourceCostSpell', operation: 'grant', value: 'Pass without Trace', cost: 2, resource: 'ki_points' },
          { type: 'resourceCostSpell', operation: 'grant', value: 'Silence',            cost: 2, resource: 'ki_points' },
        ],
        notes: 'PHB p.80 — Spend 2 ki to cast Darkness / Darkvision / Pass without Trace / Silence. Minor Illusion at-will. All wired (atWillSpell for Minor Illusion; resourceCostSpell for the four ki spells).',
      },
      'Shadow Step': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_shadow_step_teleport' },
        ],
        notes: 'PHB p.80 — Bonus action: teleport up to 60 ft from one shadow to another, gain advantage on next melee attack. Action-economy + teleport handler.',
      },
      'Cloak of Shadows': {
        minLevel: 11,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_shadow_cloak_invisible_in_shadow' },
        ],
        notes: 'PHB p.80 — Action: become invisible in dim light or darkness until you attack/cast/move into bright light. [BREADCRUMB] Conditional invisibility handler.',
      },
      'Opportunist': {
        minLevel: 17,
        effects: [],
        notes: 'PHB p.80 — Reaction: when ally within 5 ft hits a creature, make a melee attack against it. [BREADCRUMB] Reaction handler.',
      },
    },

    'Way of the Four Elements': {
      source: 'srd',
      'Disciple of the Elements': {
        minLevel: 3,
        effects: [],
        notes: 'PHB p.81 — Learn Elemental Disciplines (2 at L3, +1 at L6/11/17). Each costs ki; some replicate spells. [BREADCRUMB BC-11-style] Per-discipline effect registration as future commit.',
      },
      'Elemental Attunement': {
        minLevel: 3,
        effects: [],
        notes: 'PHB p.81 — Disciple of the Elements grants Elemental Attunement as one option (cantrip-like elemental manipulation). Narrative.',
      },
      'Extra Elemental Discipline': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.81 — Learn 1 additional Elemental Discipline (also at L11/17). [BREADCRUMB] Discipline picker UI.',
      },
    },

    'Way of Mercy': {
      source: 'tashas',
      'Implements of Mercy': {
        minLevel: 3,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'skill:insight', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'skill:medicine', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'tool:herbalism_kit', proficiencyLevel: 1 },
        ],
        notes: 'TCE p.49 — Proficiency in Insight + Medicine + herbalism kit; gain a unique mask.',
      },
      'Hand of Healing': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_mercy_hand_of_healing' },
        ],
        notes: 'TCE p.49 — Spend 1 ki to heal a creature 1d4 + monk level HP via touch (or replace one Flurry attack). [BREADCRUMB] Ki-spend heal handler.',
      },
      'Hand of Harm': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_mercy_hand_of_harm' },
        ],
        notes: 'TCE p.49 — When you hit with unarmed strike, spend 1 ki for 1d6 + WIS mod necrotic damage (1/turn). [BREADCRUMB] Combat-tracker damage rider on unarmed.',
      },
      "Physician's Touch": {
        minLevel: 6,
        effects: [],
        notes: 'TCE p.49 — Hand of Healing also ends 1 disease/condition; Hand of Harm imposes poisoned (CON save end) until end of next turn.',
      },
      'Flurry of Healing and Harm': {
        minLevel: 11,
        effects: [],
        notes: 'TCE p.49 — When you Flurry of Blows, can replace each unarmed strike with Hand of Healing without spending ki; can use Hand of Harm 1× as part of Flurry without spending bonus ki.',
      },
      'Hand of Ultimate Mercy': {
        minLevel: 17,
        effects: [],
        notes: 'TCE p.49 — Action: spend 5 ki to revive a creature dead ≤24 hours (CON+monk level HP). 1/long rest. [BREADCRUMB] Resurrection action handler.',
      },
    },

    'Way of the Astral Self': {
      source: 'tashas',
      'Arms of the Astral Self': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_astral_arms' },
        ],
        notes: 'TCE p.49 — Spend 1 ki bonus action: manifest spectral arms (10ft reach, WIS mod replaces STR for unarmed/grapple). [BREADCRUMB] Combat-tracker reach + ability override.',
      },
      'Visage of the Astral Self': {
        minLevel: 6,
        effects: [],
        notes: 'TCE p.50 — Spend 1 ki to manifest visage: 120 ft darkvision; speak with anyone in 600 ft they understand a language; advantage on Intimidation/Persuasion. [BREADCRUMB] Conditional senses + skill advantage.',
      },
      'Body of the Astral Self': {
        minLevel: 11,
        effects: [],
        notes: 'TCE p.50 — While Arms + Visage active: when hit, can use reaction to reduce damage by 1d10 + WIS + Monk level; once per turn extra +1d6 force damage on Arms attack.',
      },
      'Awakening of the Astral Self': {
        minLevel: 17,
        effects: [],
        notes: 'TCE p.50 — Bonus action: full Astral Self for 10 min (1/long rest). +2 AC, +1d10 force damage on Arms, can Sanctuary creature within 10 ft as bonus action.',
      },
    },

    'Way of the Drunken Master': {
      source: 'xanathars',
      'Bonus Proficiencies': {
        minLevel: 3,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'skill:performance', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'tool:brewers_supplies', proficiencyLevel: 1 },
        ],
        notes: "XGE p.34 — Performance skill + brewer's supplies tool proficiency.",
      },
      'Drunken Technique': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_drunken_technique_disengage' },
        ],
        notes: 'XGE p.34 — When you Flurry of Blows, gain Disengage benefit + walking speed +10 until end of turn. [BREADCRUMB] Combat-tracker movement rider.',
      },
      'Tipsy Sway': {
        minLevel: 6,
        effects: [],
        notes: 'XGE p.34 — Spend 1 ki reaction to redirect missed attack to another creature within 5 ft (Redirect Attack); stand from prone as 5 ft of movement (Leap to Your Feet). [BREADCRUMB] Reaction redirect.',
      },
      "Drunkard's Luck": {
        minLevel: 11,
        effects: [],
        notes: 'XGE p.34 — Spend 2 ki to cancel disadvantage on attack/check/save (no advantage instead). [BREADCRUMB] Roll modifier.',
      },
      'Intoxicated Frenzy': {
        minLevel: 17,
        effects: [],
        notes: 'XGE p.34 — When you Flurry, can make up to 3 additional unarmed strikes (max 3 different targets across the flurry).',
      },
    },

    'Way of the Kensei': {
      source: 'xanathars',
      'Path of the Kensei': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_kensei_weapons' },
        ],
        notes: 'XGE p.34 — Choose 2 weapons (one melee non-heavy/non-special, one ranged) as Kensei weapons (count as monk weapons). Bonus action AC+2 with Kensei weapon. Bonus action defense reaction. [BREADCRUMB] Per-weapon-tag tagging.',
      },
      'Kensei Weapons': {
        minLevel: 3,
        effects: [],
        notes: 'XGE p.34 — Path of the Kensei subhead: weapons chosen here count as monk weapons for unarmed-die scaling. [BREADCRUMB] Weapon-as-monk-weapon flag.',
      },
      'One with the Blade': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_kensei_magical_attacks' },
        ],
        notes: 'XGE p.34 — Magic Kensei weapons: Kensei attacks count as magical for resistance/immunity. Deft Strike: spend 1 ki to add martial arts die to weapon damage (1/turn).',
      },
      'Sharpen the Blade': {
        minLevel: 11,
        effects: [],
        notes: 'XGE p.34 — Bonus action: spend 1-3 ki to grant +ki spent to weapon attack/damage for 1 minute.',
      },
      'Unerring Accuracy': {
        minLevel: 17,
        effects: [],
        notes: 'XGE p.34 — Once per turn: re-roll a missed monk-weapon attack.',
      },
    },

    'Way of the Sun Soul': {
      source: 'xanathars',
      'Radiant Sun Bolt': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_sun_soul_radiant_bolt' },
        ],
        notes: 'XGE p.34 — Ranged spell attack 30 ft → 1d4+DEX radiant; counts as monk weapon for Flurry/Patient Defense. [BREADCRUMB] Ranged attack + monk-weapon flag.',
      },
      'Searing Arc Strike': {
        minLevel: 6,
        effects: [],
        notes: 'XGE p.34 — Spend 2+ ki bonus action after Attack action: cast Burning Hands at level (1+ki spent above 2). [BREADCRUMB] Ki-cast spell mechanic.',
      },
      'Searing Sunburst': {
        minLevel: 11,
        effects: [],
        notes: 'XGE p.34 — Action: 20-ft sphere within 150 ft → CON save or 2d6 radiant. Spend ki to add 2d6 per ki (max 3 ki).',
      },
      'Sun Shield': {
        minLevel: 17,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monk_sun_soul_sun_shield' },
        ],
        notes: 'XGE p.34 — Magical sun aura: bright light 30 ft, dim 30 ft beyond. When hit by melee attacker within 5 ft, reaction → 5+WIS radiant damage. [BREADCRUMB] Aura light + reaction damage.',
      },
    },
  },
  Paladin: {
    'Oath of Devotion': {
      source: 'srd',
      'Channel Divinity: Sacred Weapon': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_devotion_sacred_weapon' },
        ],
        notes: 'PHB p.86 — Channel Divinity option. As an action, imbue one weapon with positive energy: +CHA mod (min +1) to attack rolls and emits 20-ft bright light + 20-ft dim light for 1 minute. Magical for the duration. Resource via classResources Paladin Channel Divinity. [BREADCRUMB] Combat panel toggle for sacred-weapon attack/damage bonus + duration tracker.',
      },
      'Channel Divinity: Turn the Unholy': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_devotion_turn_unholy' },
        ],
        notes: 'PHB p.86 — Channel Divinity option. Action: each fiend/undead within 30 ft must make WIS save or be turned for 1 minute. Save-DC + condition handler in combat panel.',
      },
      'Aura of Devotion': {
        minLevel: 7,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_devotion_aura_charm_immunity' },
        ],
        notes: 'PHB p.86 — You and friendly creatures within 10 ft (30 ft at L18) can\'t be charmed while you\'re conscious. Aura-radius scaling at L18 is narrative for now. [BREADCRUMB] Aura-immunity consumer + radius tier scaling.',
      },
      'Purity of Spirit': {
        minLevel: 15,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_devotion_purity_of_spirit' },
        ],
        notes: 'PHB p.86 — Always under the effects of Protection from Evil and Good (no concentration). [BREADCRUMB] Auto-applied permanent buff registered as feature flag.',
      },
      'Holy Nimbus': {
        minLevel: 20,
        effects: [],
        notes: 'PHB p.86 — Action: emanate sunlight (30 ft) for 1 minute. Fiends/undead in aura take 10 radiant at start of their turns; advantage on saves vs spells by fiends/undead. 1/long rest. [BREADCRUMB] Long-buff toggle + per-turn aura damage handler.',
      },
    },
    'Oath of the Ancients': {
      source: 'srd',
      "Channel Divinity: Nature's Wrath": {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_ancients_natures_wrath' },
        ],
        notes: 'PHB p.87 — Channel Divinity option. Spectral vines restrain a creature within 10 ft (STR or DEX save, save again at end of each turn). Save-DC + restrained-condition handler.',
      },
      'Channel Divinity: Turn the Faithless': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_ancients_turn_faithless' },
        ],
        notes: 'PHB p.87 — Channel Divinity option. Action: each fey/fiend within 30 ft must make WIS save or be turned for 1 minute (and reveal true form if shapechanger). Save-DC + condition handler.',
      },
      'Aura of Warding': {
        minLevel: 7,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_ancients_aura_warding_spell_resistance' },
        ],
        notes: 'PHB p.87 — You and friendly creatures within 10 ft (30 ft at L18) have resistance to damage from spells. Encoded as feature flag because the registry "resistance" type expects damage-type strings (mapping table line 1001), and "spell" is a source category not a damage type. [BREADCRUMB] Spell-source resistance consumer in damage pipeline.',
      },
      'Undying Sentinel': {
        minLevel: 15,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_ancients_undying_sentinel' },
        ],
        notes: 'PHB p.87 — When reduced to 0 HP and not killed outright, drop to 1 HP instead. 1/long rest. Also no longer ages and can\'t be aged magically. [BREADCRUMB] Death-prevention reaction-style hook + age-immunity narrative.',
      },
      'Elder Champion': {
        minLevel: 20,
        effects: [],
        notes: 'PHB p.87 — Action: assume an ancient form for 1 minute (regen 10 HP/turn, cast spells as bonus action, enemies within 10 ft have disadvantage on saves vs paladin spells/CD). 1/long rest. [BREADCRUMB] Long-buff toggle + per-turn regen + aura-disadvantage consumer.',
      },
    },
    'Oath of Vengeance': {
      source: 'srd',
      'Channel Divinity: Abjure Enemy': {
        minLevel: 3,
        effects: [],
        notes: "PHB p.88 — Channel Divinity option. Resource via classResources Paladin Channel Divinity. Per-option effect on toggle (BC-11-style ticket).",
      },
      'Channel Divinity: Vow of Enmity': {
        minLevel: 3,
        effects: [
          { type: 'advantage', operation: 'advantage', target: 'attack:vowed', condition: { when: 'attackingVowedTarget' } },
        ],
        notes: 'PHB p.88 — Advantage on attacks vs vowed target for 1 minute. Combat panel toggles attackingVowedTarget when player declares.',
      },
      'Relentless Avenger': {
        minLevel: 7,
        effects: [],
        notes: 'PHB p.88 — On opportunity attack hit, move up to half speed as part of reaction. [BREADCRUMB] OA reaction handler.',
      },
      'Soul of Vengeance': {
        minLevel: 15,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_vengeance_soul_reaction', condition: { when: 'attackingVowedTarget' } },
        ],
        notes: 'PHB p.88 — Reaction: melee attack against vowed target when they attack. Reaction-handler flag.',
      },
      'Avenging Angel': {
        minLevel: 20,
        effects: [],
        notes: "PHB p.88 — Action: angel form for 1 hour, fly speed 60, frightened aura. [BREADCRUMB] Long-buff toggle + duration tracking.",
      },
    },
    'Oath of Glory': {
      source: 'tashas',
      'Channel Divinity: Peerless Athlete': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_glory_peerless_athlete' },
        ],
        notes: "TCE p.50 — Channel Divinity option. Bonus action: advantage on STR (Athletics) and DEX (Acrobatics) checks for 10 minutes; carrying capacity doubled and you can lift creatures up to your size. [BREADCRUMB] Skill-advantage toggle + duration tracker.",
      },
      'Channel Divinity: Inspiring Smite': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_glory_inspiring_smite' },
        ],
        notes: "TCE p.50 — Channel Divinity option. After Divine Smite, distribute 2d8 + paladin level temp HP among creatures within 30 ft. [BREADCRUMB] Post-smite hook + temp-HP distributor.",
      },
      'Aura of Alacrity': {
        minLevel: 7,
        effects: [
          { type: 'speed', operation: 'add', value: 10 },
        ],
        notes: "TCE p.50 — Your speed +10. Friendly creatures starting their turn within 5 ft (10 ft at L18) gain +10 speed until end of turn. Self-bonus is registered; friend-aura is narrative. [BREADCRUMB] Aura speed-grant consumer + radius tier scaling.",
      },
      'Glorious Defense': {
        minLevel: 15,
        effects: [],
        notes: "TCE p.50 — Reaction: when you or creature within 10 ft is hit by attack, add CHA mod to AC (potentially turning hit into miss); on turning to miss, you can make one weapon attack. PB/long rest uses. [BREADCRUMB] Reaction-AC swap + counter-attack handler.",
      },
      'Living Legend': {
        minLevel: 20,
        effects: [],
        notes: "TCE p.50 — Bonus action: 1 minute aura. Become magical, advantage on CHA checks; once/turn force re-roll on missed attacks; failed save can be re-rolled (1/long rest). [BREADCRUMB] Long-buff toggle + miss-reroll + save-reroll handler.",
      },
    },
    'Oath of the Watchers': {
      source: 'tashas',
      "Channel Divinity: Watcher's Will": {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_watchers_watchers_will' },
        ],
        notes: "TCE p.51 — Channel Divinity option. Bonus action: choose up to CHA mod creatures within 30 ft; for 1 minute they have advantage on INT/WIS/CHA saves. [BREADCRUMB] Allied-buff aura + duration.",
      },
      'Channel Divinity: Abjure the Extraplanar': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_watchers_abjure_extraplanar' },
        ],
        notes: "TCE p.51 — Channel Divinity option. Action: each aberration/celestial/elemental/fey/fiend within 30 ft must make WIS save or be turned for 1 minute. Save-DC + condition handler.",
      },
      'Aura of the Sentinel': {
        minLevel: 7,
        effects: [],
        notes: "TCE p.51 — You and creatures within 10 ft (30 ft at L18) gain +PB to initiative. [BREADCRUMB] Initiative-bonus consumer (effect type currently doesn't model initiative; encode as breadcrumb until BC adds it).",
      },
      'Vigilant Rebuke': {
        minLevel: 15,
        effects: [],
        notes: "TCE p.51 — Reaction: when you or creature within 30 ft succeeds on INT/WIS/CHA save, deal 2d8+CHA force damage to triggering creature. [BREADCRUMB] Save-success reaction handler.",
      },
      'Mortal Bulwark': {
        minLevel: 20,
        effects: [],
        notes: "TCE p.51 — Bonus action: 1 minute. Truesight 120 ft; advantage on attacks vs aberration/celestial/elemental/fey/fiend; on hit force WIS save or banish to home plane. 1/long rest. [BREADCRUMB] Long-buff + truesight-grant + on-hit save handler.",
      },
    },
    'Oath of Conquest': {
      source: 'xanathars',
      'Channel Divinity: Conquering Presence': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_conquest_conquering_presence' },
        ],
        notes: 'XGE p.38 — Channel Divinity option. Action: each creature within 30 ft must make WIS save or be frightened for 1 minute. Save-DC + frightened-condition handler.',
      },
      'Channel Divinity: Guided Strike': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_conquest_guided_strike' },
        ],
        notes: 'XGE p.38 — Channel Divinity option. +10 to an attack roll (declared after roll, before result known). [BREADCRUMB] Post-roll attack-bonus consumer.',
      },
      'Aura of Conquest': {
        minLevel: 7,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_conquest_aura_conquest' },
        ],
        notes: 'XGE p.38 — Frightened creatures within 10 ft (30 ft at L18) have speed 0 and take psychic damage = half paladin level at start of their turns. [BREADCRUMB] Aura-frightened-debuff consumer.',
      },
      'Scornful Rebuke': {
        minLevel: 15,
        effects: [],
        notes: 'XGE p.38 — Creature that hits you with attack takes psychic damage = CHA mod (min 1). [BREADCRUMB] On-hit retaliation damage consumer.',
      },
      'Invincible Conqueror': {
        minLevel: 20,
        effects: [],
        notes: 'XGE p.38 — Action: 1 minute. Resistance to all damage; one extra attack on Attack action; melee crits on 19-20. 1/long rest. [BREADCRUMB] Long-buff toggle + extra-attack + crit-range modulation.',
      },
    },
    'Oath of Redemption': {
      source: 'xanathars',
      'Channel Divinity: Emissary of Peace': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_redemption_emissary_of_peace' },
        ],
        notes: 'XGE p.39 — Channel Divinity option. Bonus action: +5 to Persuasion checks for 10 minutes. [BREADCRUMB] Skill-bonus toggle + duration.',
      },
      'Channel Divinity: Rebuke the Violent': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'paladin_redemption_rebuke_the_violent' },
        ],
        notes: 'XGE p.39 — Channel Divinity option. Reaction: when attacker within 30 ft damages another creature, attacker takes equal radiant damage (WIS save halves). [BREADCRUMB] Reaction radiant-damage handler.',
      },
      'Aura of the Guardian': {
        minLevel: 7,
        effects: [],
        notes: 'XGE p.39 — Reaction: take damage in place of an ally within 10 ft (30 ft at L18). [BREADCRUMB] Reaction damage-redirect handler.',
      },
      'Protective Spirit': {
        minLevel: 15,
        effects: [],
        notes: 'XGE p.39 — Regain 1d6 + half paladin level HP at end of turn if below half max HP and not incapacitated. [BREADCRUMB] End-of-turn auto-heal consumer.',
      },
      'Emissary of Redemption': {
        minLevel: 20,
        effects: [
          { type: 'resistance', operation: 'grant', value: 'bludgeoning', damageSource: 'nonmagical' },
          { type: 'resistance', operation: 'grant', value: 'piercing',    damageSource: 'nonmagical' },
          { type: 'resistance', operation: 'grant', value: 'slashing',    damageSource: 'nonmagical' },
        ],
        notes: 'XGE p.39 — Resistance to damage from non-allies; when creature damages you it takes equal radiant damage. damageSource:nonmagical filters at combat-resolution time for the B/P/S types. [BREADCRUMB] Non-ally damage filter + on-damage retaliation handler still future combat-tracker work.',
      },
    },
  },
  Ranger: {
    'Hunter': {
      source: 'srd',
      "Hunter's Prey": {
        minLevel: 3,
        choiceKey: 'hunterOptions.3',
        choices: {
          'Colossus Slayer': {
            effects: [
              { type: 'damageBonus', operation: 'add_dice', value: '1d8', condition: { when: 'targetBelowFullHp' } },
            ],
            // [PHASE B / #035 — 2026-05-06]: Surfaces the
            //   `targetBelowFullHp` combat toggle whenever the player
            //   has picked this option at hunterOptions.3. Closes the
            //   string-match `subclassData.hunterOptions.3 ===
            //   'Colossus Slayer'` anti-pattern in
            //   src/data/combatToggles.js.
            surfacesCombatToggle: 'targetBelowFullHp',
            notes: 'PHB p.93 — +1d8 damage to a creature below full HP, once per turn. Combat panel toggles targetBelowFullHp.',
          },
          'Giant Killer': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'hunter_giant_killer_reaction' },
            ],
            notes: 'PHB p.93 — Reaction attack vs Large+ creature within 5 ft after their attack. [BREADCRUMB BC-5] Reaction handler.',
          },
          'Horde Breaker': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'hunter_horde_breaker' },
            ],
            notes: 'PHB p.93 — Once per turn, extra weapon attack vs different target within 5 ft. [BREADCRUMB BC-6] Combat-tracker attack flow.',
          },
        },
        notes: "PHB p.93 — Choose Colossus Slayer / Giant Killer / Horde Breaker. Resolved via subclassData.hunterOptions.3.",
      },
      'Defensive Tactics': {
        minLevel: 7,
        choiceKey: 'hunterOptions.7',
        choices: {
          'Escape the Horde': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'hunter_escape_horde_oa_disadvantage' },
            ],
            notes: 'PHB p.93 — Opportunity attacks against you have disadvantage.',
          },
          'Multiattack Defense': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'hunter_multiattack_defense' },
            ],
            notes: "PHB p.93 — When hit by a creature, +4 AC vs that creature's subsequent attacks until end of turn. [BREADCRUMB] Per-attacker AC tracking.",
          },
          'Steel Will': {
            effects: [
              { type: 'advantage', operation: 'advantage', target: 'save:frightened' },
            ],
            notes: 'PHB p.93 — Advantage on saves against being frightened.',
          },
        },
        notes: 'PHB p.93 — Choose Escape the Horde / Multiattack Defense / Steel Will. Resolved via subclassData.hunterOptions.7.',
      },
      'Multiattack': {
        minLevel: 11,
        choiceKey: 'hunterOptions.11',
        choices: {
          'Volley': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'hunter_volley_ranged_aoe' },
            ],
            notes: 'PHB p.93 — Action: ranged attack vs all creatures within 10 ft of a chosen point. Action-economy. [BREADCRUMB] Combat-tracker AoE flow.',
          },
          'Whirlwind Attack': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'hunter_whirlwind_melee_aoe' },
            ],
            notes: 'PHB p.93 — Action: melee attack vs all creatures within 5 ft. Action-economy. [BREADCRUMB] Combat-tracker AoE flow.',
          },
        },
        notes: 'PHB p.93 — Choose Volley / Whirlwind Attack. Both are action-economy feature flags resolved via subclassData.hunterOptions.11.',
      },
      "Superior Hunter's Defense": {
        minLevel: 15,
        choiceKey: 'hunterOptions.15',
        choices: {
          'Evasion': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'hunter_superior_evasion' },
            ],
            notes: 'PHB p.93 — DEX save for half damage instead deals none on success / half on failure. [BREADCRUMB] Save-resolution consumer (mirrors Rogue Evasion).',
          },
          'Stand Against the Tide': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'hunter_stand_against_tide_redirect' },
            ],
            notes: 'PHB p.93 — Reaction: hostile creature missing you can be forced to redirect attack at another creature. [BREADCRUMB] Reaction handler.',
          },
          'Uncanny Dodge': {
            effects: [
              { type: 'feature', operation: 'grant', value: 'hunter_superior_uncanny_dodge' },
            ],
            notes: 'PHB p.93 — Reaction: halve damage from one attacker you can see. [BREADCRUMB] Reaction + damage-halving consumer.',
          },
        },
        notes: 'PHB p.93 — Choose Evasion / Stand Against the Tide / Uncanny Dodge. Resolved via subclassData.hunterOptions.15.',
      },
    },

    'Beast Master': {
      source: 'srd',
      "Ranger's Companion": {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'beast_master_companion' },
        ],
        notes: "PHB p.93 — Bond with an animal companion (CR 1/4 or lower, no flying speed >0). Companion sheet/management. [BREADCRUMB] Companion subsystem (BC-7) — Bucket 3 registers the feature flag only.",
      },
      'Exceptional Training': {
        minLevel: 7,
        effects: [
          { type: 'feature', operation: 'grant', value: 'beast_master_exceptional_training' },
        ],
        notes: 'PHB p.93 — On any of your turns when companion does not attack, you can use a bonus action to command it to Dash, Disengage, Dodge, or Help. Its weapon attacks count as magical. Companion subsystem.',
      },
      'Bestial Fury': {
        minLevel: 11,
        effects: [
          { type: 'feature', operation: 'grant', value: 'beast_master_bestial_fury' },
        ],
        notes: 'PHB p.93 — When you command companion to attack, it makes two attacks. Companion subsystem.',
      },
      'Share Spells': {
        minLevel: 15,
        effects: [
          { type: 'feature', operation: 'grant', value: 'beast_master_share_spells' },
        ],
        notes: 'PHB p.94 — Spells you cast targeting yourself can also affect your companion if within 30 ft. Spellcasting consumer + companion subsystem.',
      },
    },

    'Fey Wanderer': {
      source: 'tashas',
      'Dreadful Strikes': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'fey_wanderer_dreadful_strikes' },
        ],
        notes: 'TCE p.59 — Once per turn, deal extra 1d4 psychic damage with a weapon attack. Combat damage handler. [BREADCRUMB] Per-turn first-hit tracking; scales to 1d6 at L11 (handled in same flag — consumer reads ranger level).',
      },
      'Fey Wanderer Magic': {
        minLevel: 3,
        effects: [],
        notes: 'TCE p.59 — Domain-style spell-list expansion (Charm Person/Misty Step/etc.). Handled by effectCalculator.getSubclassSpells() — do NOT duplicate.',
      },
      'Otherworldly Glamour': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'fey_wanderer_glamour_cha_skill_bonus' },
        ],
        notes: 'TCE p.59 — Choose one of Deception/Performance/Persuasion: gain proficiency (or expertise if already proficient) and add WIS modifier to all CHA checks. Player makes the skill choice during creation/level-up; the proficiency grant + CHA-mod-on-CHA-checks consumer reads the choice. [BREADCRUMB] Skill-grant handler reads subclassData.glamourSkill.',
      },
      'Beguiling Twist': {
        minLevel: 7,
        effects: [
          { type: 'feature', operation: 'grant', value: 'fey_wanderer_beguiling_twist' },
        ],
        notes: 'TCE p.59 — Advantage on saves vs being charmed/frightened; reaction to redirect those effects on allies. Reaction handler + advantage-on-save consumer.',
      },
      'Fey Reinforcements': {
        minLevel: 11,
        effects: [
          // [Phase 4b BC sweep — APR 2026]: Cast Summon Fey 1/long-rest
          //   without a slot. Wired via perRestSpell — same path as Star
          //   Map / Walker in Dreams. SpellbookTab badge + cast button
          //   handle the rest.
          { type: 'perRestSpell', operation: 'grant', value: 'Summon Fey', recharge: 'long_rest', uses: 1 },
        ],
        notes: 'TCE p.59 — Learn Summon Fey; cast it once per long rest without a spell slot. Wired via perRestSpell.',
      },
      'Misty Wanderer': {
        minLevel: 15,
        effects: [
          { type: 'perRestSpell', operation: 'grant', value: 'Misty Step', recharge: 'long_rest', uses: 'wisMod' },
        ],
        notes: 'TCE p.59 — Cast Misty Step as a bonus action a number of times = WIS mod per long rest, without using a spell slot; can bring an ally within 5 ft. Spellcasting / resource consumer.',
      },
    },

    'Swarmkeeper': {
      source: 'tashas',
      'Gathered Swarm': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'swarmkeeper_gathered_swarm' },
        ],
        notes: 'TCE p.60 — When you hit with a weapon attack, swarm assists: choose force damage / forced movement / self-shift. Combat damage / movement consumer. [BREADCRUMB] Per-turn swarm-effect choice.',
      },
      'Swarmkeeper Magic': {
        minLevel: 3,
        effects: [],
        notes: 'TCE p.60 — Spell-list expansion (Mage Hand cantrip + Faerie Fire/etc.). Handled by effectCalculator.getSubclassSpells() — do NOT duplicate.',
      },
      'Writhing Tide': {
        minLevel: 7,
        effects: [
          { type: 'feature', operation: 'grant', value: 'swarmkeeper_writhing_tide_fly' },
        ],
        notes: 'TCE p.60 — Bonus action: gain 10 ft fly speed + hover for 1 minute, prof bonus uses per long rest. Speed/movement consumer + resource. [BREADCRUMB] Temporary fly speed handler.',
      },
      'Mighty Swarm': {
        minLevel: 11,
        effects: [
          { type: 'feature', operation: 'grant', value: 'swarmkeeper_mighty_swarm_upgrade' },
        ],
        notes: 'TCE p.60 — Gathered Swarm upgrades: damage 1d8 / forced movement +5ft / self-shift grants prone-immunity-on-shift. Combat damage handler.',
      },
      'Swarming Dispersal': {
        minLevel: 15,
        effects: [
          { type: 'feature', operation: 'grant', value: 'swarmkeeper_swarming_dispersal' },
        ],
        notes: 'TCE p.60 — Reaction: when hit, gain resistance to that damage and teleport up to 30 ft. Reaction handler + situational resistance.',
      },
    },

    'Gloom Stalker': {
      source: 'xanathars',
      'Dread Ambusher': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'gloom_stalker_dread_ambusher' },
        ],
        notes: "XGE p.42 — +WIS mod to initiative; on first turn of combat, +10 ft speed and one extra weapon attack that deals +1d8. Initiative consumer + first-turn attack/speed bonus. [BREADCRUMB] First-turn-of-combat tracker.",
      },
      'Umbral Sight': {
        minLevel: 3,
        effects: [
          { type: 'sense', operation: 'grant', target: 'darkvision', value: 60 },
          { type: 'feature', operation: 'grant', value: 'gloom_stalker_umbral_sight_plus_30' },
        ],
        notes: "XGE p.42 — Gain 60 ft darkvision (or +30 ft if you already have it; consumer should treat the flag as 'add 30 to existing darkvision' and ignore the 60-ft sense grant if a higher race-granted darkvision already exists). Also: invisible to creatures relying on darkvision while in darkness. [BREADCRUMB] Darkvision-stacking consumer + invisibility-vs-darkvision condition. The dual registration is intentional: a baseline 60 ft sense grant covers characters without racial darkvision, while the feature flag breadcrumbs the +30 stack so SensesMovementPanel can render the correct final value.",
      },
      'Gloom Stalker Magic': {
        minLevel: 3,
        effects: [],
        notes: 'XGE p.42 — Spell-list expansion (Disguise Self/Rope Trick/etc.). Handled by effectCalculator.getSubclassSpells() — do NOT duplicate.',
      },
      'Iron Mind': {
        minLevel: 7,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'save:wisdom', proficiencyLevel: 1 },
        ],
        notes: 'XGE p.42 — Gain proficiency in Wisdom saves (or another save if you already have it). Default to WIS for registry; player overrides via subclassData.ironMindSave. [BREADCRUMB] Save-prof-conflict resolver.',
      },
      "Stalker's Flurry": {
        minLevel: 11,
        effects: [
          { type: 'feature', operation: 'grant', value: 'gloom_stalker_stalkers_flurry' },
        ],
        notes: "XGE p.42 — Once per turn, when you miss with a weapon attack, you can make another attack. Combat-tracker reroll-on-miss handler.",
      },
      'Shadowy Dodge': {
        minLevel: 15,
        effects: [
          { type: 'feature', operation: 'grant', value: 'gloom_stalker_shadowy_dodge' },
        ],
        notes: 'XGE p.42 — Reaction: impose disadvantage on an attack against you (must see attacker, not incapacitated). Reaction handler.',
      },
    },

    'Horizon Walker': {
      source: 'xanathars',
      'Detect Portal': {
        minLevel: 3,
        effects: [],
        notes: 'XGE p.42 — Action: detect existence + location of nearest planar portal within 1 mile. Once per short rest. Pure narrative / DM-facing.',
      },
      'Planar Warrior': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'horizon_walker_planar_warrior' },
        ],
        notes: 'XGE p.42 — Bonus action: mark a target; next weapon hit this turn deals weapon damage as force + extra 1d8 force (1d8 → 2d8 at L11). Combat damage handler. [BREADCRUMB] Damage-type override + per-turn mark tracker.',
      },
      'Horizon Walker Magic': {
        minLevel: 3,
        effects: [],
        notes: 'XGE p.42 — Spell-list expansion (Protection from Evil and Good/Misty Step/etc.). Handled by effectCalculator.getSubclassSpells() — do NOT duplicate.',
      },
      'Ethereal Step': {
        minLevel: 7,
        effects: [
          { type: 'feature', operation: 'grant', value: 'horizon_walker_ethereal_step' },
        ],
        notes: 'XGE p.42 — Cast Etherealness as a bonus action, but only lasts 1 turn. Once per short rest. Spellcasting / resource consumer.',
      },
      'Distant Strike': {
        minLevel: 11,
        effects: [
          { type: 'feature', operation: 'grant', value: 'horizon_walker_distant_strike' },
        ],
        notes: 'XGE p.42 — When you take Attack action, teleport up to 10 ft before each attack; if you attack at least 2 different targets, make one extra attack vs a 3rd target. Combat-tracker.',
      },
      'Spectral Defense': {
        minLevel: 15,
        effects: [
          { type: 'feature', operation: 'grant', value: 'horizon_walker_spectral_defense' },
        ],
        notes: 'XGE p.42 — Reaction: when you take damage, gain resistance to that damage type until end of next turn. Reaction handler + situational resistance.',
      },
    },

    'Monster Slayer': {
      source: 'xanathars',
      "Hunter's Sense": {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monster_slayer_hunters_sense' },
        ],
        notes: "XGE p.43 — Action: touch a creature, learn its damage immunities / resistances / vulnerabilities. WIS-mod uses per long rest. [BREADCRUMB] Information-gathering action + per-rest resource.",
      },
      "Slayer's Prey": {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monster_slayer_slayers_prey' },
        ],
        notes: "XGE p.43 — Bonus action: designate a target; first weapon hit per turn vs that target deals +1d6 damage. Combat damage handler. [BREADCRUMB] Per-turn first-hit tracking + designated-target marker.",
      },
      'Monster Slayer Magic': {
        minLevel: 3,
        effects: [],
        notes: 'XGE p.43 — Spell-list expansion (Protection from Evil and Good/Zone of Truth/etc.). Handled by effectCalculator.getSubclassSpells() — do NOT duplicate.',
      },
      'Supernatural Defense': {
        minLevel: 7,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monster_slayer_supernatural_defense' },
        ],
        notes: "XGE p.43 — When prey forces a save or hits with attack, add +1d6 to your save / damage from prey is reduced by 1d6. Save / damage-reduction consumer keyed on Slayer's Prey marker.",
      },
      "Magic-User's Nemesis": {
        minLevel: 11,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monster_slayer_magic_users_nemesis' },
        ],
        notes: 'XGE p.43 — Reaction: when a creature within 60 ft casts a spell, force WIS save or spell fails (and is wasted). Once per short rest. Reaction handler + resource.',
      },
      "Slayer's Counter": {
        minLevel: 15,
        effects: [
          { type: 'feature', operation: 'grant', value: 'monster_slayer_slayers_counter' },
        ],
        notes: "XGE p.43 — Reaction: when prey forces you to save, make weapon attack vs prey; on hit, save auto-succeeds. Reaction handler keyed on Slayer's Prey marker.",
      },
    },
  },
  Rogue: {
    'Thief': {
      source: 'srd',
      'Fast Hands': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'thief_fast_hands_cunning_action_use' },
        ],
        notes: 'PHB p.97 — Use Cunning Action bonus action to make Sleight of Hand check, use thieves\' tools, or take Use an Object action. Action-economy handler.',
      },
      'Second-Story Work': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'thief_second_story_climb_no_extra_cost' },
        ],
        notes: 'PHB p.97 — Climbing no longer costs extra movement; running jump distance increases by Dex modifier feet. [BREADCRUMB] Movement-cost + jump-distance consumer.',
      },
      'Supreme Sneak': {
        minLevel: 9,
        effects: [
          { type: 'feature', operation: 'grant', value: 'thief_supreme_sneak_advantage_stealth' },
        ],
        notes: 'PHB p.97 — Advantage on Stealth checks if you move no more than half your speed on the same turn. Conditional advantage handler.',
      },
      'Use Magic Device': {
        minLevel: 13,
        effects: [
          { type: 'feature', operation: 'grant', value: 'thief_use_magic_device_ignore_class_race_level' },
        ],
        notes: 'PHB p.97 — Ignore all class, race, and level requirements on the use of magic items. Item-attunement consumer.',
      },
      "Thief's Reflexes": {
        minLevel: 17,
        effects: [
          { type: 'feature', operation: 'grant', value: 'thief_reflexes_two_turns_round_one' },
        ],
        notes: 'PHB p.97 — Take two turns during the first round of combat (one at normal initiative, one at initiative-10). Cannot be used if surprised. Initiative handler.',
      },
    },

    'Assassin': {
      source: 'srd',
      'Bonus Proficiencies': {
        minLevel: 3,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'tool:disguise_kit', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'tool:poisoners_kit', proficiencyLevel: 1 },
        ],
        notes: "PHB p.97 — Disguise kit and poisoner's kit proficiency.",
      },
      'Assassinate': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'assassin_advantage_vs_no_initiative' },
          { type: 'criticalRange', operation: 'min', value: 2, condition: { when: 'attackingSurprisedTarget' } },
        ],
        notes: 'PHB p.97 — Advantage on attacks vs creatures that have not taken a turn yet; any hit vs surprised creature is a crit. criticalRange:2 = always-crit when surprised.',
      },
      'Infiltration Expertise': {
        minLevel: 9,
        effects: [],
        notes: 'PHB p.97 — Spend 25gp + 7 days to assume a false identity. Narrative.',
      },
      'Impostor': {
        minLevel: 13,
        effects: [],
        notes: "PHB p.97 — Mimic another person's speech/writing/behavior. Narrative.",
      },
      'Death Strike': {
        minLevel: 17,
        effects: [],
        notes: 'PHB p.97 — Surprise hit doubles damage. [BREADCRUMB] Combat-tracker damage doubling.',
      },
    },

    'Arcane Trickster': {
      source: 'srd',
      'Spellcasting': {
        minLevel: 3,
        effects: [],
        notes: 'PHB p.97 — Third-caster Wizard list (Enchantment/Illusion only except 3rd/8th/14th/20th). Spell list expansion handled by effectCalculator.getSubclassSpells().',
      },
      'Mage Hand Legerdemain': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'arcane_trickster_mage_hand_invisible_legerdemain' },
        ],
        notes: 'PHB p.98 — Invisible mage hand can stow/retrieve items, pour, pick locks, disarm traps. Skill-check consumer.',
      },
      'Magical Ambush': {
        minLevel: 9,
        effects: [
          { type: 'feature', operation: 'grant', value: 'arcane_trickster_magical_ambush_disadvantage_save' },
        ],
        notes: 'PHB p.98 — If hidden when you cast a spell, target has disadvantage on the saving throw. Save-resolution consumer.',
      },
      'Versatile Trickster': {
        minLevel: 13,
        effects: [
          { type: 'feature', operation: 'grant', value: 'arcane_trickster_versatile_trickster_advantage_via_mage_hand' },
        ],
        notes: 'PHB p.98 — Use mage hand bonus action to distract a creature within 5 ft of it: gain advantage on attacks vs that creature this turn. Combat-tracker consumer.',
      },
      'Spell Thief': {
        minLevel: 17,
        effects: [
          { type: 'feature', operation: 'grant', value: 'arcane_trickster_spell_thief_steal_spell' },
        ],
        notes: 'PHB p.98 — Reaction when a creature casts a spell at you: WIS save (DC 8+prof+INT). On fail, spell fizzles and you learn it for 8 hours. Once per long rest. Reaction handler.',
      },
    },

    'Phantom': {
      source: 'tashas',
      'Whispers of the Dead': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'phantom_whispers_dead_swap_proficiency' },
        ],
        notes: 'TCE p.62 — Whenever you finish a short or long rest, gain proficiency in one skill or tool of your choice (replaces previous Whispers proficiency). Proficiency-swap consumer.',
      },
      'Wails from the Grave': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'phantom_wails_grave_necrotic_burst' },
        ],
        notes: 'TCE p.62 — When you Sneak Attack, deal half-Sneak-Attack necrotic damage to a second creature within 30 ft. Prof-bonus uses per long rest. Combat-tracker consumer.',
      },
      'Tokens of the Departed': {
        minLevel: 9,
        effects: [
          { type: 'feature', operation: 'grant', value: 'phantom_tokens_departed_soul_trinket' },
        ],
        notes: 'TCE p.62 — Create a Soul Trinket on creature death; advantage on death saves and CON saves while holding one; consume to ask the dead a question. Item/resource consumer.',
      },
      'Ghost Walk': {
        minLevel: 13,
        effects: [
          { type: 'feature', operation: 'grant', value: 'phantom_ghost_walk_incorporeal' },
        ],
        notes: 'TCE p.62 — Bonus action: assume a ghostly form for 10 min (10 ft fly speed, hover, half damage from non-force/non-radiant, pass through objects). Action-economy + resistance handler.',
      },
      "Death's Friend": {
        minLevel: 17,
        effects: [
          { type: 'feature', operation: 'grant', value: 'phantom_deaths_friend_full_sneak_attack_aoe' },
        ],
        notes: "TCE p.62 — Wails from the Grave deals full Sneak Attack damage; you always have at least one Soul Trinket. Combat-tracker upgrade.",
      },
    },

    'Soulknife': {
      source: 'tashas',
      'Psionic Power': {
        minLevel: 3,
        effects: [],
        notes: 'TCE p.63 — Gain Psionic Energy dice (resource handled via classResources.js Rogue:Soulknife). Powers: Psi-Bolstered Knack, Psychic Whispers.',
      },
      'Psychic Blades': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'soulknife_psychic_blades_manifest' },
        ],
        notes: 'TCE p.63 — Manifest psychic blades as finesse/thrown (60/120) simple melee weapons; psychic damage; Sneak Attack works with them. Weapon-system consumer.',
      },
      'Soul Blades': {
        minLevel: 9,
        effects: [
          { type: 'feature', operation: 'grant', value: 'soulknife_soul_blades_homing_strikes_psychic_teleport' },
        ],
        notes: 'TCE p.63 — Homing Strikes (spend psionic die to turn miss into hit) and Psychic Teleportation (spend die, throw blade, teleport up to die roll x10 ft). Combat + movement consumer.',
      },
      'Psychic Veil': {
        minLevel: 13,
        effects: [
          { type: 'feature', operation: 'grant', value: 'soulknife_psychic_veil_invisibility' },
        ],
        notes: 'TCE p.63 — Action: become invisible for 1 hour or until you deal damage / force a save. Once per long rest (or expend a psionic die). Stealth consumer.',
      },
      'Rend Mind': {
        minLevel: 17,
        effects: [
          { type: 'feature', operation: 'grant', value: 'soulknife_rend_mind_stun_on_sneak' },
        ],
        notes: 'TCE p.63 — When you Sneak Attack with Psychic Blades, force WIS save or be stunned for 1 minute (re-save end of turn). Once per long rest unless you spend 3 psionic dice. Save-resolution consumer.',
      },
    },

    'Inquisitive': {
      source: 'xanathars',
      'Ear for Deceit': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'inquisitive_ear_for_deceit_min_8_insight' },
        ],
        notes: 'XGE p.45 — Treat any Insight roll lower than 8 as an 8 when determining if a creature is lying. Skill-check floor consumer.',
      },
      'Eye for Detail': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'inquisitive_eye_for_detail_perception_investigate_bonus_action' },
        ],
        notes: 'XGE p.45 — Bonus action to make a Perception check to spot a hidden creature/object or to make an Investigation check to discern clues. Action-economy consumer.',
      },
      'Insightful Fighting': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'inquisitive_insightful_fighting_sneak_without_advantage' },
        ],
        notes: 'XGE p.45 — Bonus action: Insight (DC 8+CHA(deception)+prof) vs target. On success, you can apply Sneak Attack to that target without needing advantage (still must use a finesse/ranged weapon, no disadvantage). Sneak-attack consumer.',
      },
      'Steady Eye': {
        minLevel: 9,
        effects: [
          { type: 'feature', operation: 'grant', value: 'inquisitive_steady_eye_advantage_after_half_move' },
        ],
        notes: 'XGE p.45 — Advantage on Perception/Investigation if you move no more than half your speed on the same turn. Conditional advantage consumer.',
      },
      'Unerring Eye': {
        minLevel: 13,
        effects: [
          { type: 'feature', operation: 'grant', value: 'inquisitive_unerring_eye_sense_illusion_shapechange' },
        ],
        notes: 'XGE p.45 — Action: sense illusions, shapechangers, and other magical disguises within 30 ft. Wisdom-mod uses per long rest. Sense consumer.',
      },
      'Eye for Weakness': {
        minLevel: 17,
        effects: [
          { type: 'feature', operation: 'grant', value: 'inquisitive_eye_for_weakness_extra_3d6_sneak' },
        ],
        notes: 'XGE p.45 — While Insightful Fighting is active on a target, your Sneak Attack vs that target deals an extra 3d6 damage. Combat-tracker consumer.',
      },
    },

    'Mastermind': {
      source: 'xanathars',
      'Master of Intrigue': {
        minLevel: 3,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'tool:disguise_kit', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'tool:forgery_kit', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'language:any_1', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'language:any_2', proficiencyLevel: 1 },
          { type: 'feature', operation: 'grant', value: 'mastermind_mimic_speech_patterns' },
        ],
        notes: "XGE p.46 — Disguise kit + forgery kit proficiency, two additional languages, and mimic speech patterns/accents. Languages encoded as language:any_N placeholders pending custom-language-pick UI.",
      },
      'Master of Tactics': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'mastermind_help_30ft_bonus_action' },
        ],
        notes: 'XGE p.46 — Use Help action as a bonus action; range increases to 30 ft. Action-economy consumer.',
      },
      'Insightful Manipulator': {
        minLevel: 9,
        effects: [
          { type: 'feature', operation: 'grant', value: 'mastermind_insightful_manipulator_learn_stats' },
        ],
        notes: 'XGE p.46 — Spend 1 minute observing a humanoid: learn 2 of (INT score, WIS score, CHA score, class levels). Narrative-skill consumer.',
      },
      'Misdirection': {
        minLevel: 13,
        effects: [
          { type: 'feature', operation: 'grant', value: 'mastermind_misdirection_redirect_attack' },
        ],
        notes: 'XGE p.46 — When targeted by an attack while a creature within 5 ft of you provides cover, you can use a reaction to have the attack target that creature instead. Reaction handler.',
      },
      'Soul of Deceit': {
        minLevel: 17,
        effects: [
          { type: 'feature', operation: 'grant', value: 'mastermind_soul_deceit_thoughts_immune' },
        ],
        notes: 'XGE p.46 — Thoughts cannot be read by any means; magical lie detection always reads you as truthful; can pass any Zone of Truth. [BREADCRUMB] Mind-reading immunity flag.',
      },
    },

    'Scout': {
      source: 'xanathars',
      'Skirmisher': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'scout_skirmisher_reaction_move_half_speed' },
        ],
        notes: 'XGE p.46 — Reaction when an enemy ends its turn within 5 ft of you: move up to half your speed. Movement does not provoke opportunity attacks from that enemy. Reaction handler.',
      },
      'Survivalist': {
        minLevel: 3,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'skill:nature', proficiencyLevel: 2 },
          { type: 'proficiency', operation: 'grant', value: 'skill:survival', proficiencyLevel: 2 },
        ],
        notes: 'XGE p.46 — Proficiency (and expertise) in Nature and Survival. Already-proficient skills get expertise (proficiencyLevel:2 = double prof).',
      },
      'Superior Mobility': {
        minLevel: 9,
        effects: [
          { type: 'speed', operation: 'add', value: 10 },
        ],
        notes: 'XGE p.46 — Walking speed increases by 10. If you have a climb or swim speed, increase that one too. (Climb/swim parity is a [BREADCRUMB] — speed registry only models walking add today.)',
      },
      'Ambush Master': {
        minLevel: 13,
        effects: [
          { type: 'feature', operation: 'grant', value: 'scout_ambush_master_initiative_advantage_first_attacked_disadv' },
        ],
        notes: 'XGE p.46 — Advantage on initiative; first creature you hit in a round has disadvantage on attacks vs anyone but you until the start of your next turn. Combat-tracker consumer.',
      },
      'Sudden Strike': {
        minLevel: 17,
        effects: [
          { type: 'feature', operation: 'grant', value: 'scout_sudden_strike_bonus_attack_sneak_eligible' },
        ],
        notes: 'XGE p.46 — Bonus action attack that can apply Sneak Attack (counts as a separate turn for Sneak Attack purposes). Action-economy consumer.',
      },
    },

    'Swashbuckler': {
      source: 'xanathars',
      'Fancy Footwork': {
        minLevel: 3,
        effects: [
          { type: 'feature', operation: 'grant', value: 'swashbuckler_fancy_footwork_no_opportunity' },
        ],
        notes: 'XGE p.47 — Creatures you melee-attack on your turn cannot make opportunity attacks against you the rest of that turn. Combat-tracker consumer.',
      },
      'Rakish Audacity': {
        minLevel: 3,
        effects: [
          { type: 'initiative', operation: 'add', value: 'cha_modifier' },
          { type: 'feature', operation: 'grant', value: 'swashbuckler_rakish_audacity_sneak_no_advantage_solo' },
        ],
        notes: 'XGE p.47 — Add CHA modifier to initiative. Sneak Attack works without advantage when no other creature is within 5 ft of the target (still need finesse/ranged + no disadvantage). Sneak-attack consumer.',
      },
      'Panache': {
        minLevel: 9,
        effects: [
          { type: 'feature', operation: 'grant', value: 'swashbuckler_panache_charisma_persuasion_charm' },
        ],
        notes: 'XGE p.47 — Action: opposed Persuasion vs Insight. On success: hostile target has disadvantage on attacks vs anyone but you for 1 min; non-hostile target is charmed for 1 min (ends if attacked). Skill-resolution consumer.',
      },
      'Elegant Maneuver': {
        minLevel: 13,
        effects: [
          { type: 'feature', operation: 'grant', value: 'swashbuckler_elegant_maneuver_advantage_acrobatics_athletics' },
        ],
        notes: 'XGE p.47 — Bonus action: gain advantage on the next Acrobatics or Athletics check you make this turn. Action-economy + skill-check consumer.',
      },
      'Master Duelist': {
        minLevel: 17,
        effects: [
          { type: 'feature', operation: 'grant', value: 'swashbuckler_master_duelist_reroll_missed_attack' },
        ],
        notes: 'XGE p.47 — Reroll a missed attack roll with advantage. Once per short or long rest. Reaction/recharge consumer.',
      },
    },
  },
  Sorcerer: {
    'Draconic Bloodline': {
      source: 'srd',
      'Dragon Ancestor': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'draconic_dragon_ancestor' },
        ],
        notes: 'PHB p.102 — Choose 1 dragon ancestor (stored on subclassData.dragonAncestor). You learn Draconic; double proficiency on CHA checks vs dragons. Language grant + dragon-targeted CHA-check bonus are [BREADCRUMB] consumers — feature flag exposes the bonus to skill rolls.',
      },
      'Draconic Resilience': {
        minLevel: 1,
        effects: [
          { type: 'hp', operation: 'add', value: 'sorcererLevel*1' },
          // [Phase 4b BC-9 fix — APR 2026]: PHB p.102 says "AC equals 13 + DEX
          //   modifier" when unarmored. Previously emitted as set:13 which
          //   silently dropped DEX (Draconic Sorcerer with DEX 14 got AC 13
          //   instead of 14 — subtle but RAW-incorrect). The unarmored base
          //   AC is 10 + DEX, so add:3 raises it to 13 + DEX exactly. Stacks
          //   with no other unarmored-AC source by RAW (Mage Armor / Barbarian
          //   UA etc. are also "set the base", not "stack on top").
          { type: 'ac', operation: 'add', value: 3, condition: { when: 'unarmored' } },
        ],
        notes: 'PHB p.102 — Max HP increases by 1 per Sorcerer level. When not wearing armor, AC is 13 + DEX modifier. [BREADCRUMB] If multiclassed, the +1/level only applies to Sorcerer levels (formula sorcererLevel*1 already enforces this).',
      },
      'Elemental Affinity': {
        minLevel: 6,
        effects: [
          { type: 'damageBonus', operation: 'add', value: 'cha_modifier', condition: { when: 'castingDraconicElementSpell' } },
        ],
        notes: 'PHB p.102 — When casting a spell whose damage type matches your draconic ancestor, add CHA modifier to one damage roll. Spend 1 sorcery point to gain resistance to that damage type for 1 hour (resource consumer). The castingDraconicElementSpell flag is flipped by the spellCastingService when the cast spell\'s damage type matches subclassData.dragonAncestor → dragonOptions.damageType.',
      },
      'Dragon Wings': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'draconic_dragon_wings_fly_speed' },
        ],
        notes: 'PHB p.102 — Bonus action: sprout dragon wings, gaining a fly speed equal to your walking speed. Speed-system [BREADCRUMB] — fly_speed = walking_speed mirror is a separate consumer (BC-19).',
      },
      'Draconic Presence': {
        minLevel: 18,
        effects: [
          { type: 'feature', operation: 'grant', value: 'draconic_presence_aura' },
        ],
        notes: 'PHB p.102 — Action + 5 sorcery points: project an aura of awe or fear (60 ft, 1 minute, WIS save). Charm/fear-aura combat consumer.',
      },
    },

    'Wild Magic': {
      source: 'srd',
      'Wild Magic Surge': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wild_magic_surge' },
        ],
        notes: 'PHB p.103 — After casting a level-1+ Sorcerer spell, DM may have you roll d20; on 1, roll on the Wild Magic Surge table. Surge-table consumer (DM-side). [BREADCRUMB] Surge-table data + roller (BC-9).',
      },
      'Tides of Chaos': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wild_magic_tides_of_chaos' },
        ],
        notes: 'PHB p.103 — 1/long rest, gain advantage on one attack/check/save. After use, DM may force a Wild Magic Surge roll (refunds the use). Resource consumer + DM-trigger consumer.',
      },
      'Bend Luck': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wild_magic_bend_luck' },
        ],
        notes: 'PHB p.103 — Reaction + 2 sorcery points: roll 1d4 and add or subtract from another creature\'s attack/check/save. Reaction consumer.',
      },
      'Controlled Chaos': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wild_magic_controlled_chaos' },
        ],
        notes: 'PHB p.103 — When rolling on the Wild Magic Surge table, roll twice and pick the result you prefer. Surge-roller consumer.',
      },
      'Spell Bombardment': {
        minLevel: 18,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wild_magic_spell_bombardment' },
        ],
        notes: 'PHB p.103 — Once per turn, when rolling damage and any die rolls max value, roll one additional die of that type and add it. Damage-roller consumer.',
      },
    },

    'Aberrant Mind': {
      source: 'tashas',
      'Psionic Spells': {
        minLevel: 1,
        effects: [],
        notes: 'TCE p.66 — Gain 6 spells from the Aberrant Mind Psionic Spells list (1 cantrip-equiv + 5 spells via expanded learning). Already wired through effectCalculator.getSubclassSpells() — no registry effect needed (mapping table line 1009).',
      },
      'Telepathic Speech': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'aberrant_telepathic_speech' },
        ],
        notes: 'TCE p.66 — Form telepathic link with a creature within 30 ft for up to (CHA mod) minutes. Action-economy [BREADCRUMB].',
      },
      'Psionic Sorcery': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'aberrant_psionic_sorcery_subtle' },
        ],
        notes: 'TCE p.66 — Cast Psionic Spells by spending sorcery points instead of a slot. Spells cast this way are subtle (no V/S components) and can\'t be detected by counterspell. Spell-cast consumer + sorcery-point cost gate.',
      },
      'Psychic Defenses': {
        minLevel: 6,
        effects: [
          { type: 'resistance', operation: 'grant', value: 'psychic' },
          { type: 'advantage', operation: 'advantage', target: 'save:charm' },
          { type: 'advantage', operation: 'advantage', target: 'save:fear' },
        ],
        notes: 'TCE p.66 — Resistance to psychic damage; advantage on saves vs charmed and frightened conditions.',
      },
      'Revelation in Flesh': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'aberrant_revelation_in_flesh' },
        ],
        notes: 'TCE p.66 — Sorcery point cost: gain 1+ benefits (swim+darkvision, fly, squeeze, see-invisible) for 10 minutes. Resource consumer + sense/movement [BREADCRUMB].',
      },
      'Warping Implosion': {
        minLevel: 18,
        effects: [
          { type: 'feature', operation: 'grant', value: 'aberrant_warping_implosion' },
        ],
        notes: 'TCE p.67 — Action + 5 sorcery points: teleport up to 120 ft and deal 6d10 force damage in 30-ft radius (DEX save half). Once per short rest. Combat consumer.',
      },
    },

    'Clockwork Soul': {
      source: 'tashas',
      'Clockwork Magic': {
        minLevel: 1,
        effects: [],
        notes: 'TCE p.68 — Gain Clockwork spells (alarm, protection from evil and good, etc.) per the Clockwork Magic table. Already wired through effectCalculator.getSubclassSpells() — no registry effect needed.',
      },
      'Restore Balance': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'clockwork_restore_balance' },
        ],
        notes: 'TCE p.68 — Reaction (PB/long rest): cancel advantage or disadvantage on a roll within 60 ft. Reaction consumer.',
      },
      'Bastion of Law': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'clockwork_bastion_of_law' },
        ],
        notes: 'TCE p.68 — Action + 1-5 sorcery points: ward target with N d8 dice that absorb damage. Resource consumer + damage-absorption [BREADCRUMB].',
      },
      'Trance of Order': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'clockwork_trance_of_order' },
        ],
        notes: 'TCE p.69 — Bonus action (1/long rest, refresh by 5 sorcery points): for 1 minute, treat any d20 roll of 9 or lower as a 10. Roll-modifier [BREADCRUMB].',
      },
      'Clockwork Cavalcade': {
        minLevel: 18,
        effects: [
          { type: 'feature', operation: 'grant', value: 'clockwork_cavalcade' },
        ],
        notes: 'TCE p.69 — Action (1/long rest, refresh by 7 sorcery points): heal 100 HP among allies, end up to 6 magical effects (level 6+), repair objects in 30 ft. Combat/utility consumer.',
      },
    },

    'Divine Soul': {
      source: 'xanathars',
      'Divine Magic': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'divine_soul_divine_magic' },
        ],
        notes: 'XGE p.50 — Choose alignment-affinity (good/evil/lawful/chaotic/neutral) → +1 free spell from cleric list at L1. Cleric spell list expanded into Sorcerer\'s known spells. [BREADCRUMB] Cleric-list expansion handled by getSubclassSpells follow-up.',
      },
      'Favored by the Gods': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'divine_soul_favored_by_gods' },
        ],
        notes: 'XGE p.50 — When you fail an attack or save, add 2d4 to the roll (1/short or long rest). Resource consumer.',
      },
      'Empowered Healing': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'divine_soul_empowered_healing' },
        ],
        notes: 'XGE p.50 — Spend 1 sorcery point to reroll healing dice on a spell you (or an ally within 5 ft) cast. 1/turn. Heal-modifier consumer.',
      },
      'Otherworldly Wings': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'divine_soul_otherworldly_wings' },
        ],
        notes: 'XGE p.50 — Bonus action: sprout wings, fly speed = walking speed. Speed-system [BREADCRUMB] — fly_speed = walking_speed mirror (BC-19).',
      },
      'Unearthly Recovery': {
        minLevel: 18,
        effects: [
          { type: 'feature', operation: 'grant', value: 'divine_soul_unearthly_recovery' },
        ],
        notes: 'XGE p.50 — Bonus action (1/long rest): when at half HP or less, regain HP equal to half your max. Heal/threshold consumer.',
      },
    },

    'Shadow Magic': {
      source: 'xanathars',
      'Eyes of the Dark': {
        minLevel: 1,
        effects: [
          { type: 'sense', operation: 'grant', target: 'darkvision', value: 120 },
          { type: 'feature', operation: 'grant', value: 'shadow_eyes_pierce_magical_darkness' },
          // [Phase 4b BC-13 closeout — APR 2026]: 2-sorcery-point Darkness.
          //   The "see normally in that darkness" rider is a feature flag on
          //   the line above; the resource-cost cast is wired here.
          { type: 'resourceCostSpell', operation: 'grant', value: 'Darkness', cost: 2, resource: 'sorcery_points' },
        ],
        notes: 'XGE p.51 — Darkvision 120 ft that sees through magical darkness. Spend 2 sorcery points to cast Darkness without a slot (wired via resourceCostSpell). The "see normally in that darkness" rider stays a feature flag — magical-darkness-piercing requires a sense-line consumer.',
      },
      'Strength of the Grave': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'shadow_strength_of_the_grave' },
        ],
        notes: 'XGE p.51 — When reduced to 0 HP (not crit / not radiant), CHA save DC 5+damage to drop to 1 HP instead. 1/long rest. HP-threshold consumer.',
      },
      'Hound of Ill Omen': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'shadow_hound_of_ill_omen' },
        ],
        notes: 'XGE p.51 — Bonus action + 3 sorcery points: summon a shadowy dire wolf (target gets disadvantage on saves vs your spells). Summon consumer.',
      },
      'Shadow Walk': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'shadow_walk_teleport' },
        ],
        notes: 'XGE p.51 — Bonus action: teleport up to 120 ft from one area of dim light/darkness to another. Movement consumer.',
      },
      'Umbral Form': {
        minLevel: 18,
        effects: [
          { type: 'feature', operation: 'grant', value: 'shadow_umbral_form' },
        ],
        notes: 'XGE p.51 — Bonus action + 6 sorcery points: become shadowy for 1 minute (resistance to all damage except force/radiant; can move through occupied space). Resistance/movement consumer.',
      },
    },

    'Storm Sorcery': {
      source: 'xanathars',
      'Wind Speaker': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'storm_wind_speaker_languages' },
        ],
        notes: 'XGE p.51 — You can speak, read, and write Primordial (and its dialects: Aquan, Auran, Ignan, Terran). Language-grant [BREADCRUMB] — proficiency.language consumer.',
      },
      'Tempestuous Magic': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'storm_tempestuous_fly_after_cast' },
        ],
        notes: 'XGE p.51 — Before or after casting a level-1+ spell, fly 10 ft without provoking opportunity attacks. Movement consumer.',
      },
      'Heart of the Storm': {
        minLevel: 6,
        effects: [
          { type: 'resistance', operation: 'grant', value: 'lightning' },
          { type: 'resistance', operation: 'grant', value: 'thunder' },
          { type: 'feature', operation: 'grant', value: 'storm_heart_aura_damage' },
        ],
        notes: 'XGE p.51 — Resistance to lightning and thunder damage. When casting a level-1+ spell of those types, deal half-sorcerer-level lightning/thunder damage to creatures within 10 ft. Aura damage is a [BREADCRUMB] consumer (combat panel).',
      },
      'Storm Guide': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'storm_guide_weather_control' },
        ],
        notes: 'XGE p.51 — Action: stop rain in 20-ft radius around you; bonus action: choose wind direction (100-ft radius). Environmental/utility consumer.',
      },
      "Storm's Fury": {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'storm_fury_reactive_lightning' },
        ],
        notes: 'XGE p.51 — Reaction when hit by melee: deal sorcerer-level lightning damage; attacker STR save or be pushed 20 ft. Reaction consumer.',
      },
      'Wind Soul': {
        minLevel: 18,
        effects: [
          { type: 'resistance', operation: 'immunity', value: 'lightning' },
          { type: 'resistance', operation: 'immunity', value: 'thunder' },
          { type: 'feature', operation: 'grant', value: 'storm_wind_soul_fly_60' },
        ],
        notes: 'XGE p.51 — Immunity to lightning and thunder damage (operation:immunity surfaces them to getDamageImmunities). Fly speed 60 ft. Bonus action 1/long rest: reduce fly speed to 30 to grant 6 allies fly 30 for 1 hour.',
      },
    },
  },
  Warlock: {
    'The Fiend': {
      source: 'srd',
      "Dark One's Blessing": {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'fiend_dark_ones_blessing_temp_hp_on_kill' },
        ],
        notes: 'PHB p.109 — When you reduce a hostile creature to 0 HP, gain temporary HP equal to your CHA modifier + Warlock level (min 1). Combat-tracker on-kill consumer; temp-HP application happens via combat panel listening to this flag.',
      },
      "Dark One's Own Luck": {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'fiend_dark_ones_own_luck_d10_to_save_or_check' },
        ],
        notes: 'PHB p.109 — When you make an ability check or saving throw, you can add a d10 to the roll. Once per short or long rest. Per-rest tracker + roll-augment consumer.',
      },
      'Fiendish Resilience': {
        minLevel: 10,
        effects: [
          { type: 'feature', operation: 'grant', value: 'fiend_fiendish_resilience_choose_resistance_after_rest' },
        ],
        notes: 'PHB p.109 — After a short or long rest, choose a damage type. You have resistance to that damage until you choose a different one (overcome by magical attacks). [BREADCRUMB] Player-pick after rest UI + dynamic resistance grant.',
      },
      'Hurl Through Hell': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'fiend_hurl_through_hell_psychic_10d10' },
        ],
        notes: 'PHB p.109 — On hit, send target on a brief tour through the Lower Planes: 10d10 psychic damage. Once per long rest. Per-rest tracker + on-hit consumer.',
      },
    },

    'The Archfey': {
      source: 'srd',
      'Fey Presence': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'archfey_fey_presence_15ft_charm_or_fear' },
        ],
        notes: 'PHB p.109 — Action: each creature in 10-ft cube WIS save or be charmed or frightened (your choice) for 1 minute. Once per short or long rest. Per-rest tracker + AoE save consumer.',
      },
      'Misty Escape': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'archfey_misty_escape_reaction_teleport' },
        ],
        notes: 'PHB p.109 — Reaction when you take damage: turn invisible and teleport up to 60 ft. Once per short or long rest. Per-rest tracker + reaction consumer.',
      },
      'Beguiling Defenses': {
        minLevel: 10,
        effects: [
          { type: 'feature', operation: 'grant', value: 'archfey_beguiling_defenses_charm_immunity_reflect' },
        ],
        notes: 'PHB p.109 — Immunity to being charmed; when a creature attempts to charm you, reaction to make them WIS save vs your spell save DC or be charmed by you for 1 minute (encoded as feature flag because the registry does not currently distinguish immunity from resistance — mapping table line 1001).',
      },
      'Dark Delirium': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'archfey_dark_delirium_charm_or_fear_phasm' },
        ],
        notes: 'PHB p.109 — Action: 1 creature in 60 ft WIS save or be charmed or frightened (your choice) for 1 minute, perceiving surroundings as a phasm. Once per long rest. Per-rest tracker + AoE save consumer.',
      },
    },

    'The Great Old One': {
      source: 'srd',
      'Awakened Mind': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'great_old_one_awakened_mind_telepathy_30ft' },
        ],
        notes: 'PHB p.110 — Telepathically communicate with any creature within 30 ft you can see (one-way, recipient need not understand a language). Communication consumer.',
      },
      'Entropic Ward': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'great_old_one_entropic_ward_disadv_reaction' },
        ],
        notes: 'PHB p.110 — Reaction when attacked: impose disadvantage on the attack. If the attack misses, your next attack vs that creature has advantage. Once per short or long rest. Per-rest tracker + reaction consumer.',
      },
      'Thought Shield': {
        minLevel: 10,
        effects: [
          { type: 'resistance', operation: 'grant', value: 'psychic' },
          { type: 'feature', operation: 'grant', value: 'great_old_one_thought_shield_psychic_redirect' },
        ],
        notes: 'PHB p.110 — Thoughts cannot be read by telepathy or other means without your permission. Resistance to psychic damage; whenever a creature deals psychic damage to you, it takes the same damage. The damage-reflect part is encoded as a feature flag (registry has no reflect mechanic yet — separate consumer).',
      },
      'Create Thrall': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'great_old_one_create_thrall_charm_incapacitated' },
        ],
        notes: 'PHB p.110 — Action: touch an incapacitated humanoid to charm it indefinitely. The charm only ends if you use this feature on a different creature, or your patron commands it. Action + charm consumer.',
      },
    },

    'The Fathomless': {
      source: 'tashas',
      'Tentacle of the Deeps': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'fathomless_tentacle_of_the_deeps_cold_damage' },
        ],
        notes: "TCE p.71 — Bonus action: summon a spectral 10-ft tentacle within 60 ft. Lasts 1 minute, attacks deal 1d8 cold damage and reduce target's speed by 10. Proficiency-bonus uses per long rest. Per-rest tracker + summon consumer.",
      },
      'Gift of the Sea': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'fathomless_gift_of_the_sea_swim_breathe_water' },
        ],
        notes: 'TCE p.71 — Swim speed of 40 and ability to breathe underwater. Encoded as feature flag because the speed registry does not yet model swim-speed grants (BC for swim-speed parity; mapping table line 1005).',
      },
      'Oceanic Soul': {
        minLevel: 6,
        effects: [
          { type: 'resistance', operation: 'grant', value: 'cold' },
          { type: 'feature', operation: 'grant', value: 'fathomless_oceanic_soul_aquan_speech_underwater' },
        ],
        notes: 'TCE p.71 — Resistance to cold damage. While underwater you can communicate telepathically with any creature within 60 ft, and you understand Aquan/Primordial. The communication part is encoded as a feature flag.',
      },
      'Guardian Coil': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'fathomless_guardian_coil_reaction_reduce_damage' },
        ],
        notes: 'TCE p.72 — Reaction when a creature within 10 ft of your tentacle takes damage: tentacle reduces damage by 1d8 (2d8 at L10). Reaction + tentacle consumer.',
      },
      'Grasping Tentacles': {
        minLevel: 10,
        effects: [
          { type: 'feature', operation: 'grant', value: 'fathomless_grasping_tentacles_evards_black_tentacles' },
          // [Phase 4b BC sweep — APR 2026]: 1/long-rest free Evard's Black
          //   Tentacles cast wired via perRestSpell. Spell list expansion
          //   (so the spell appears in the spellbook) is handled separately
          //   by getSubclassSpells; this only handles the free-cast tracker.
          { type: 'perRestSpell', operation: 'grant', value: "Evard's Black Tentacles", recharge: 'long_rest', uses: 1 },
        ],
        notes: 'TCE p.72 — Learn Evard\'s Black Tentacles; can cast it once per long rest without a slot. Free cast wired via perRestSpell; spell-list expansion handled by getSubclassSpells.',
      },
      'Fathomless Plunge': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'fathomless_plunge_teleport_5_creatures' },
        ],
        notes: 'TCE p.72 — Action: you and up to 5 willing creatures within 30 ft teleport to a body of water within 1 mile. Once per short or long rest. Per-rest tracker + teleport consumer.',
      },
    },

    'The Genie': {
      source: 'tashas',
      "Genie's Vessel": {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'genie_vessel_bonus_action_enter' },
        ],
        notes: "TCE p.73 — You possess a Tiny vessel (lamp/bottle/ring) tied to your patron. Bonus action: vanish into the vessel for hours up to twice your prof bonus per long rest. The genie kind (Dao/Djinni/Efreeti/Marid) is stored on subclassData.genieKind. Per-rest tracker + vessel-state consumer.",
      },
      "Genie's Wrath": {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'genie_wrath_prof_bonus_damage_once_per_turn' },
        ],
        notes: "TCE p.73 — Once per turn on a hit, deal extra damage equal to your proficiency bonus. The damage type matches your genie kind (Dao=bludgeoning, Djinni=thunder, Efreeti=fire, Marid=cold). Encoded as a feature flag because the registry does not yet support once-per-turn damageBonus with type-matching from a stored choice. [BREADCRUMB] Per-turn tracker + type-aware damageBonus consumer.",
      },
      'Elemental Gift': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'genie_elemental_gift_resistance_and_fly' },
        ],
        notes: 'TCE p.73 — Resistance to a damage type determined by your genie kind (Dao=bludgeoning, Djinni=thunder, Efreeti=fire, Marid=cold). Bonus action: gain fly speed 30 ft for 10 min, prof-bonus uses per long rest. Encoded as a feature flag because the registry-level resistance must be selected from subclassData.genieKind at registration time (current registry layer does not key resistance by external subclassData yet). [BREADCRUMB] Resistance-by-genieKind consumer + per-rest fly tracker.',
      },
      'Sanctuary Vessel': {
        minLevel: 10,
        effects: [
          { type: 'feature', operation: 'grant', value: 'genie_sanctuary_vessel_extended_rest_in_vessel' },
        ],
        notes: "TCE p.73 — When you enter the vessel via Genie's Vessel, you can take up to 5 willing creatures with you. Short rest in the vessel grants temp HP and a damage-bonus boost (CHA mod) to next-turn attacks for everyone inside. Per-rest tracker + vessel-state + AoE temp-HP consumer.",
      },
      'Limited Wish': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'genie_limited_wish_once_per_long_rest' },
        ],
        notes: 'TCE p.73 — Action: entreat your patron for a wish. Cast any spell of 6th level or lower (no components, no concentration) once per 1d4 long rests. Per-rest tracker + spell-cast consumer.',
      },
    },

    'The Celestial': {
      source: 'xanathars',
      'Bonus Cantrips': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'celestial_bonus_cantrips_light_sacred_flame' },
        ],
        notes: 'XGE p.55 — Learn Light and Sacred Flame as bonus cantrips (do not count against cantrips known). Encoded as a feature flag — spell-list expansion handled by getSubclassSpells/cantrip-grant consumer.',
      },
      'Healing Light': {
        minLevel: 1,
        effects: [
          { type: 'feature', operation: 'grant', value: 'celestial_healing_light_pool' },
        ],
        notes: 'XGE p.55 — Pool of d6 healing dice equal to 1 + Warlock level (recovered on long rest). Bonus action: spend up to CHA-mod (min 1) dice from the pool to heal a creature within 60 ft. Resource definition lives in classResources.js under Warlock:The Celestial (id healing_light_dice).',
      },
      'Radiant Soul': {
        minLevel: 6,
        effects: [
          { type: 'resistance', operation: 'grant', value: 'radiant' },
          { type: 'feature', operation: 'grant', value: 'celestial_radiant_soul_cha_radiant_or_fire_bonus' },
        ],
        notes: 'XGE p.55 — Resistance to radiant damage. When you cast a spell that deals radiant or fire damage, add CHA modifier to one damage roll. The conditional damage bonus is encoded as a feature flag because the registry does not yet support damageBonus gated on spell-damage-type matching (similar pattern to Draconic Elemental Affinity but for two types instead of one). [BREADCRUMB] castingRadiantOrFireSpell flag + damageBonus consumer.',
      },
      'Celestial Resilience': {
        minLevel: 10,
        effects: [
          { type: 'feature', operation: 'grant', value: 'celestial_resilience_temp_hp_on_rest' },
        ],
        notes: 'XGE p.55 — Whenever you finish a short or long rest, gain temp HP equal to Warlock level + CHA mod, and you can grant equal-to-half-Warlock-level temp HP to up to 5 creatures of your choice. Per-rest tracker + temp-HP grant consumer.',
      },
      'Searing Vengeance': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'celestial_searing_vengeance_self_revive_radiant' },
        ],
        notes: 'XGE p.55 — When you fail a death save, you can immediately spring up with HP equal to half your max + CHA mod, all creatures within 30 ft take 2d8+CHA radiant damage and are blinded until end of next turn. Once per long rest. Per-rest tracker + death-save consumer.',
      },
    },

    'The Hexblade': {
      source: 'xanathars',
      "Hexblade's Curse": {
        minLevel: 1,
        effects: [
          { type: 'criticalRange', operation: 'min', value: 19, condition: { when: 'attackingHexbladeCursedTarget' } },
          { type: 'damageBonus', operation: 'add', value: 'proficiency_bonus', condition: { when: 'attackingHexbladeCursedTarget' } },
          { type: 'feature', operation: 'grant', value: 'hexblade_curse_hp_regain_on_kill' },
        ],
        notes: "XGE p.55 — Bonus action: curse a creature you can see within 30 ft for 1 minute. Against the cursed target your weapon attacks crit on 19-20, you add your proficiency bonus to damage rolls, and if the cursed target dies you regain HP = Warlock level + CHA mod. Once per short or long rest. The criticalRange and damageBonus effects are gated on the attackingHexbladeCursedTarget flag (combat-toggle declared per-attack via _activeFlags); HP-regain-on-kill is encoded as a feature flag because the registry does not yet support on-event HP gain. [BREADCRUMB] Per-rest tracker + on-kill HP consumer.",
      },
      'Hex Warrior': {
        minLevel: 1,
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'armor:medium', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'armor:shield', proficiencyLevel: 1 },
          { type: 'proficiency', operation: 'grant', value: 'weapon:martial', proficiencyLevel: 1 },
          { type: 'feature', operation: 'grant', value: 'hexblade_hex_warrior_cha_attack' },
        ],
        notes: "XGE p.55 — Gain proficiency in medium armor, shields, and martial weapons. Once per long rest, choose one weapon you are proficient with that lacks the two-handed property and use CHA instead of STR/DEX for attack and damage rolls with that weapon. The CHA-attack ability is encoded as a feature flag because the registry-level attack-stat substitution must be keyed to a specific weapon stored on subclassData (not a global override). [BREADCRUMB] Hex-bound weapon CHA-attack consumer.",
      },
      'Accursed Specter': {
        minLevel: 6,
        effects: [
          { type: 'feature', operation: 'grant', value: 'hexblade_accursed_specter_raise_humanoid' },
        ],
        notes: 'XGE p.55 — When you slay a humanoid, you can raise its spirit as a specter loyal to you. Specter has temp HP = Warlock level + CHA mod and gains your proficiency bonus on attack rolls. Once per long rest, lasts until long rest or specter destroyed. Per-rest tracker + summon consumer.',
      },
      'Armor of Hexes': {
        minLevel: 10,
        effects: [
          { type: 'feature', operation: 'grant', value: 'hexblade_armor_of_hexes_50_pct_miss_cursed' },
        ],
        notes: "XGE p.55 — When the target of your Hexblade's Curse hits you with an attack, roll d6 — on 4+ the attack misses regardless of the roll. The 50% miss chance is encoded as a feature flag because the registry does not yet model post-hit miss-conversion. [BREADCRUMB] Curse-bound miss-chance reaction consumer.",
      },
      'Master of Hexes': {
        minLevel: 14,
        effects: [
          { type: 'feature', operation: 'grant', value: 'hexblade_master_of_hexes_recurse_on_kill' },
        ],
        notes: "XGE p.55 — When the target of your Hexblade's Curse dies, you can transfer the curse to a new target within 30 ft as a no-action without expending a use. Per-curse tracker + on-kill transfer consumer.",
      },
    },
  },
  Wizard: {
    'School of Abjuration': {
      source: 'srd',
      'Abjuration Savant': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wizard_abjuration_savant_copy_cost' },
        ],
        notes: 'PHB p.115 — Half cost + half time to copy abjuration spells into spellbook. [BREADCRUMB] Spellbook copy-cost consumer.',
      },
      'Arcane Ward': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wizard_abjuration_arcane_ward' },
        ],
        notes: 'PHB p.115 — When you cast abjuration spell of L1+, gain ward HP = 2 × wizard level + INT mod (max). Ward absorbs damage before HP. Wired as a class resource (Wizard:School of Abjuration arcane_ward in classResources.js, rechargeOn:never since it persists across rests). Player manages via the +/- buttons in ClassResourcesPanel; auto-refill on abjuration cast is a future ticket. RAW pool semantics (separate from temp HP) — homebrew "treat as temp HP / largest overrides" interpretation is also valid since the player chooses how to apply incoming damage.',
      },
      'Projected Ward': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.115 — Reaction: use Arcane Ward to absorb damage taken by another creature within 30 ft. [BREADCRUMB] Reaction handler.',
      },
      'Improved Abjuration': {
        minLevel: 10,
        effects: [],
        notes: 'PHB p.115 — Add proficiency bonus to ability checks made as part of casting abjuration spells (e.g., Counterspell/Dispel Magic check).',
      },
      'Spell Resistance': {
        minLevel: 14,
        effects: [
          { type: 'advantage', operation: 'advantage', target: 'save:spell' },
          { type: 'feature', operation: 'grant', value: 'wizard_abjuration_spell_resistance' },
        ],
        notes: 'PHB p.115 — Advantage on saves vs spells; resistance to damage from spells. [BREADCRUMB] Spell-damage distinction not yet modeled — registered as advantage on save:spell + feature flag for resistance consumer.',
      },
    },

    'School of Conjuration': {
      source: 'srd',
      'Conjuration Savant': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wizard_conjuration_savant_copy_cost' },
        ],
        notes: 'PHB p.116 — Half cost + half time to copy conjuration spells.',
      },
      'Minor Conjuration': {
        minLevel: 2,
        effects: [],
        notes: 'PHB p.116 — Action: conjure a non-magical object up to 3 ft³ / 10 lb for 1 hour. [BREADCRUMB] Action handler.',
      },
      'Benign Transposition': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.116 — Action: teleport up to 30 ft, or swap with a willing Med/Small creature within 30 ft. [BREADCRUMB] Teleport handler + per-rest tracker.',
      },
      'Focused Conjuration': {
        minLevel: 10,
        effects: [],
        notes: 'PHB p.116 — Concentration on conjuration spells cannot be broken by damage.',
      },
      'Durable Summons': {
        minLevel: 14,
        effects: [],
        notes: 'PHB p.116 — Conjuration summons get 30 temp HP.',
      },
    },

    'School of Divination': {
      source: 'srd',
      'Divination Savant': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wizard_divination_savant_copy_cost' },
        ],
        notes: 'PHB p.116 — Half cost + half time to copy divination spells.',
      },
      'Portent': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wizard_divination_portent' },
        ],
        notes: 'PHB p.116 — Long rest: roll 2d20 (3 at L14). Replace any creature\'s d20 roll with a portent die. Resource via classResources Wizard:School of Divination.',
      },
      'Expert Divination': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.116 — Casting a divination spell of L2+ regains a lower-level spell slot (one slot of lower level, max 5).',
      },
      'The Third Eye': {
        minLevel: 10,
        effects: [],
        notes: 'PHB p.116 — Action (1/short rest): grant self one of darkvision 60ft, see invisible, ethereal sight, read any language. [BREADCRUMB] Per-rest sense buff.',
      },
      'Greater Portent': {
        minLevel: 14,
        effects: [],
        notes: 'PHB p.116 — Portent dice rolled = 3 instead of 2 at long rest.',
      },
    },

    'School of Enchantment': {
      source: 'srd',
      'Enchantment Savant': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wizard_enchantment_savant_copy_cost' },
        ],
        notes: 'PHB p.117 — Half cost + half time to copy enchantment spells.',
      },
      'Hypnotic Gaze': {
        minLevel: 2,
        effects: [],
        notes: 'PHB p.117 — Action: charm + incapacitate creature within 5 ft (WIS save). 1/long rest. [BREADCRUMB] Action + per-rest tracker.',
      },
      'Instinctive Charm': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.117 — Reaction when targeted by attack within 30 ft: WIS save or attacker chooses different target. [BREADCRUMB] Reaction handler.',
      },
      'Split Enchantment': {
        minLevel: 10,
        effects: [],
        notes: 'PHB p.117 — Single-target enchantment spells of L1+ can target two creatures within range (must be within 30 ft of each other).',
      },
      'Alter Memories': {
        minLevel: 14,
        effects: [],
        notes: 'PHB p.117 — When you charm a creature, can also alter their memory of being charmed (1+CHA mod hours / minutes).',
      },
    },

    'School of Evocation': {
      source: 'srd',
      'Evocation Savant': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wizard_evocation_savant_copy_cost' },
        ],
        notes: 'PHB p.117 — Half cost + half time to copy evocation spells.',
      },
      'Sculpt Spells': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wizard_evocation_sculpt_spells' },
        ],
        notes: 'PHB p.117 — Casting evocation spell affecting allies: those allies auto-succeed save and take no damage. [BREADCRUMB] Spell-resolution ally-skip rider.',
      },
      'Potent Cantrip': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.117 — Half damage on saved cantrips (was none).',
      },
      'Empowered Evocation': {
        minLevel: 10,
        effects: [
          { type: 'damageBonus', operation: 'add', value: 'intelligence_modifier', condition: { when: 'castingEvocationSpellDamage' } },
        ],
        notes: 'PHB p.117 — Add INT mod to one damage roll of any wizard evocation spell you cast. [BREADCRUMB] castingEvocationSpellDamage flag set by spellCastingService for evocation cast site. Combat panel + spellCastingService consume in BC ticket.',
      },
      'Overchannel': {
        minLevel: 14,
        effects: [],
        notes: 'PHB p.117 — When casting wizard spell L1-5, deal max damage; second use before long rest takes 2d12 necrotic per spell level (escalating).',
      },
    },

    'School of Illusion': {
      source: 'srd',
      'Illusion Savant': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wizard_illusion_savant_copy_cost' },
        ],
        notes: 'PHB p.118 — Half cost + half time to copy illusion spells.',
      },
      'Improved Minor Illusion': {
        minLevel: 2,
        effects: [],
        notes: 'PHB p.118 — Learn Minor Illusion (no spell-known cost). Can create both image and sound with single casting.',
      },
      'Malleable Illusions': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.118 — Action: change nature of an active illusion (limited to original spell parameters).',
      },
      'Illusory Self': {
        minLevel: 10,
        effects: [],
        notes: 'PHB p.118 — Reaction: when hit, interpose an illusion → attacker must reroll (miss). 1/short rest. [BREADCRUMB] Reaction handler.',
      },
      'Illusory Reality': {
        minLevel: 14,
        effects: [],
        notes: 'PHB p.118 — Make one part of an illusion you cast real for 1 minute.',
      },
    },

    'School of Necromancy': {
      source: 'srd',
      'Necromancy Savant': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wizard_necromancy_savant_copy_cost' },
        ],
        notes: 'PHB p.118 — Half cost + half time to copy necromancy spells.',
      },
      'Grim Harvest': {
        minLevel: 2,
        effects: [],
        notes: 'PHB p.118 — When you kill with spell of L1+, regain HP = 2*spell level (3*spell level for necromancy). [BREADCRUMB] Spell-kill heal trigger.',
      },
      'Undead Thralls': {
        minLevel: 6,
        effects: [],
        notes: 'PHB p.119 — Animate Dead added to spellbook; raised undead get bonus HP + damage on attacks.',
      },
      'Inured to Undeath': {
        minLevel: 10,
        effects: [
          { type: 'resistance', operation: 'grant', value: 'necrotic' },
          { type: 'feature', operation: 'grant', value: 'wizard_necromancy_hp_max_protected' },
        ],
        notes: 'PHB p.119 — Resistance to necrotic damage; HP maximum cannot be reduced. [BREADCRUMB] HP-max-protect consumer.',
      },
      'Command Undead': {
        minLevel: 14,
        effects: [],
        notes: 'PHB p.119 — Action: target undead within 60 ft with CHA save or fall under your command (lasts indefinitely).',
      },
    },

    'School of Transmutation': {
      source: 'srd',
      'Transmutation Savant': {
        minLevel: 2,
        effects: [
          { type: 'feature', operation: 'grant', value: 'wizard_transmutation_savant_copy_cost' },
        ],
        notes: 'PHB p.119 — Half cost + half time to copy transmutation spells.',
      },
      'Minor Alchemy': {
        minLevel: 2,
        effects: [],
        notes: 'PHB p.119 — Spend 10 min/cubic-ft to transform one substance into another (wood→stone etc.) for 1 hour.',
      },
      "Transmuter's Stone": {
        minLevel: 6,
        effects: [],
        notes: "PHB p.119 — Spend 8 hr to create a Transmuter's Stone granting carrier one benefit (darkvision 60 / +10 speed / proficiency in CON saves / resistance acid/cold/fire/lightning/thunder). [BREADCRUMB] Held-by-carrier item buff.",
      },
      'Shapechanger': {
        minLevel: 10,
        effects: [],
        notes: 'PHB p.119 — Polymorph added to spellbook; cast Polymorph on self without slot, into a beast (CR ≤1). Per-short-rest.',
      },
      'Master Transmuter': {
        minLevel: 14,
        effects: [],
        notes: "PHB p.119 — Destroy your Transmuter's Stone for one of: Major Transformation (turn 5-cubic-ft non-magical into another substance), Panacea (full heal + cure poisoned/disease), Restore Life (Raise Dead w/o components), Restore Youth (reduce age 3d10 years).",
      },
    },
  },
};

// Freeze each top-level class entry so callers can't mutate the registry at runtime.
Object.values(SRD_SUBCLASS_FEATURES).forEach(classEntry => {
  Object.values(classEntry).forEach(subclassEntry => Object.freeze(subclassEntry));
  Object.freeze(classEntry);
});
Object.freeze(SRD_SUBCLASS_FEATURES);

/**
 * Returns the array of [featureName, featureData] entries that the character has
 * unlocked for a given className + subclassName + classLevel.
 *
 * The 'source' top-level key is filtered out (it's metadata, not a feature).
 *
 * @param {string} className
 * @param {string} subclassName
 * @param {number} classLevel
 * @returns {Array<[string, object]>}
 */
// [PHASE B / #037 — 2026-05-06]: Top-level subclass keys that aren't
//   feature objects. Filtered out of getActiveSubclassFeatures so
//   downstream consumers (CharacterBasicsPanel feature display, etc.)
//   don't try to render them as features.
const SUBCLASS_NON_FEATURE_KEYS = new Set(['source', 'levelProgression']);

export function getActiveSubclassFeatures(className, subclassName, classLevel) {
  if (!className || !subclassName || typeof classLevel !== 'number') return [];
  const classEntry = SRD_SUBCLASS_FEATURES[className];
  const subEntry = classEntry?.[subclassName];
  if (!subEntry) return [];
  return Object.entries(subEntry)
    .filter(([key]) => !SUBCLASS_NON_FEATURE_KEYS.has(key))
    .filter(([_, feature]) => feature && feature.minLevel <= classLevel);
}

export default SRD_SUBCLASS_FEATURES;
